/**
 * Registre canonique des codes d'erreur / warning Kollectivia.
 *
 * Doctrine : ADR 0027 (`docs/adr/0027-i18n-localization-policy.md`).
 *   - P-2 : codes machine en EN strict, snake_case UPPERCASE.
 *   - P-3 : messages humains bilingues `{ en, fr }` self-contained.
 *   - P-4 : warnings structurés `[{ code, messages: { en, fr } }]`.
 *
 * Premier batch (Chunk 9.K.1) : codes 9.J.1 (lookup contacts).
 * Second batch (Chunk 9.K.2)  : codes 9.G phase 1 (lookup companies + projects).
 * Troisième batch (Chunk 9.J.2.B) : codes fuzzy-only low-confidence.
 * Quatrième batch (Chunk 9.K.5)   : codes globaux handler API (D58.6).
 * Cinquième batch (Chunk 9.K.6)   : codes CRUD companies/contacts/projects (D58.1).
 * Sixième batch (Chunk 9.K.7)     : codes CRUD api_keys/brokers/anomalies (D58.1 batch 2).
 * Septième batch (Chunk 9.K.8)    : codes CRUD retention/legal-holds (D58.1 batch 3).
 *
 * Convention de nommage :
 *   - Préfixe par entité — `LOOKUP_CONTACTS_*`, `LOOKUP_COMPANIES_*` (PO-Q3).
 *   - Granularité : un code par cas d'erreur distinct (PO-Q2). Pas de
 *     `VALIDATION_ERROR` générique partagé entre N cas.
 *   - `kind` :
 *       * `error`   — destiné à `error.code` (4xx / 5xx).
 *       * `warning` — destiné à `meta.warnings[].code` (200 OK + warning).
 *
 * Tests : `codes.test.ts` vérifie unicité, format snake_case UPPERCASE,
 * présence de `messages.en` ET `messages.fr`.
 *
 * Volume : tant que ≤ 50 codes (PO-Q5 ADR 0027 / PO-Q5 Chunk 9.K.1) on
 * garde un seul fichier. Au-delà → éclatement par entité avec barrel.
 */

export type ErrorCodeKind = "error" | "warning";

/**
 * Paire de messages bilingues `{ en, fr }` — surface canonique ADR 0027 P-3.
 * Réutilisée par `ErrorCodeEntry`, `BilingualErrorPayload` et
 * `BilingualWarningPayload` côté builders.
 */
export type BilingualMessages = {
  readonly en: string;
  readonly fr: string;
};

export type ErrorCodeEntry = {
  readonly kind: ErrorCodeKind;
  readonly messages: BilingualMessages;
};

/**
 * Registre canonique. Ajoute une entrée ici puis utilise
 * `buildBilingualError(code)` ou `buildBilingualWarning(code)` côté handler.
 *
 * NE PAS importer ce registre côté frontend — les builders sont la surface
 * publique. Le registre est un détail d'implémentation côté backend / shared.
 */
export const ERROR_CODES = {
  CLIENT_RESOURCE_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "This resource is not available to a client account.",
      fr: "Cette ressource n’est pas accessible à un compte client.",
    },
  },
  CLIENT_FILE_ACCESS_DENIED: {
    kind: "error",
    messages: {
      en: "You are not authorized to upload documents for this company.",
      fr: "Vous n’êtes pas autorisé à déposer des documents pour cette entreprise.",
    },
  },
  CLIENT_FILE_PUBLICATION_INVALID: {
    kind: "error",
    messages: {
      en: "This document cannot be shared with clients: check its type and company links.",
      fr: "Ce document ne peut pas être partagé avec les clients : vérifiez son type et ses rattachements à l’entreprise.",
    },
  },
  CLIENT_COMPANY_ACCESS_INVALID: {
    kind: "error",
    messages: {
      en: "The client account or company is unavailable in this brokerage.",
      fr: "Le compte client ou l’entreprise est indisponible dans ce cabinet.",
    },
  },

  // ===========================================================================
  // 9.J.1 — Lookup contacts (Chunk 9.K.1 i18n migration)
  // ===========================================================================

  /**
   * Émis par `LookupContactQuerySchema` refine quand zéro filtre fourni.
   * 400 — l'agent doit fournir au moins un filtre discriminant.
   */
  LOOKUP_CONTACTS_FILTER_REQUIRED: {
    kind: "error",
    messages: {
      en: "At least one filter is required: q, full_name, email, phone, or company_id.",
      fr: "Au moins un filtre requis : q, full_name, email, phone ou company_id.",
    },
  },

  /**
   * Émis par le handler quand `q` est seul (sans email/phone/full_name/
   * company_id) ET qu'au moins un candidat a été trouvé. 200 OK + warning
   * en `meta.warnings[]`. PO-Q4 9.J.1.
   */
  LOOKUP_CONTACTS_LOW_CONFIDENCE_Q_ONLY: {
    kind: "warning",
    messages: {
      en: "Search by `q` alone — low confidence. Confirm contact identity via a discriminating channel (email, phone) before any action.",
      fr: "Recherche par `q` seul — confidence faible. Confirmer l'identité du contact via un canal discriminant (email, phone) avant toute action.",
    },
  },

  // ===========================================================================
  // 9.G phase 1 — Lookup companies (Chunk 9.K.2 i18n migration)
  // ===========================================================================

  /**
   * Émis par le handler `GET /v1/lookup/companies` quand zéro filtre fourni
   * (post-split base/refine — le check ≥1 filtre se fait côté handler pour
   * porter ce code dédié plutôt que `validation_error` plat du errorHandler
   * global). 400 — l'agent doit fournir au moins un filtre discriminant.
   *
   * Parité stricte : `LOOKUP_CONTACTS_FILTER_REQUIRED` (9.K.1).
   */
  LOOKUP_COMPANIES_FILTER_REQUIRED: {
    kind: "error",
    messages: {
      en: "At least one filter is required: q, legal_name, postal_code, neq, phone, or email.",
      fr: "Au moins un filtre requis : q, legal_name, postal_code, neq, phone ou email.",
    },
  },

  /**
   * Émis par le handler quand `legal_name` (ou `q` assimilé) est seul
   * (sans postal_code/neq/phone/email) ET qu'au moins un candidat a été
   * trouvé. 200 OK + warning en `meta.warnings[]`. PO-Q4 9.K.2 (parité
   * `LOOKUP_CONTACTS_LOW_CONFIDENCE_Q_ONLY` 9.K.1).
   */
  LOOKUP_COMPANIES_LOW_CONFIDENCE_LEGAL_NAME_ONLY: {
    kind: "warning",
    messages: {
      en: "Search by legal_name alone — low confidence. Confirm company identity via a discriminating channel (neq, postal_code, phone, email) before any action.",
      fr: "Recherche par legal_name seul — confidence faible. Confirmer l'identité de la compagnie via un canal discriminant (neq, postal_code, phone, email) avant toute action.",
    },
  },

  // ===========================================================================
  // 9.G phase 1 — Lookup projects (Chunk 9.K.2 i18n migration)
  // ===========================================================================

  /**
   * Émis par le handler `GET /v1/lookup/projects` quand zéro filtre fourni
   * (post-split base/refine). 400 — l'agent doit fournir au moins un
   * filtre discriminant.
   */
  LOOKUP_PROJECTS_FILTER_REQUIRED: {
    kind: "error",
    messages: {
      en: "At least one filter is required: reference_code, q, company_id, or effect_date.",
      fr: "Au moins un filtre requis : reference_code, q, company_id ou effect_date.",
    },
  },

  /**
   * Émis par le handler quand `q` (assimilé `title`) est seul (sans
   * reference_code/company_id/effect_date) ET qu'au moins un candidat a
   * été trouvé. 200 OK + warning en `meta.warnings[]`. PO-Q4 9.K.2.
   *
   * Note : `reference_code` produit un court-circuit déterministe (score
   * 1.0) — pas concerné par ce warning.
   */
  LOOKUP_PROJECTS_LOW_CONFIDENCE_TITLE_ONLY: {
    kind: "warning",
    messages: {
      en: "Search by title alone — low confidence. Confirm project identity via a discriminating channel (reference_code, company_id, effect_date) before any action.",
      fr: "Recherche par title seul — confidence faible. Confirmer l'identité du projet via un canal discriminant (reference_code, company_id, effect_date) avant toute action.",
    },
  },

  // ===========================================================================
  // 9.J.2.B — Fuzzy trigram lookup (warnings émis quand TOUS les candidats
  // retournés proviennent UNIQUEMENT du canal trigram fuzzy — aucun
  // exact-match. L'agent IA doit confirmer via canal discriminant avant
  // toute action.) Score fuzzy plafonné à 0.7 (PO-Q2) — n'atteint jamais
  // `high`.
  // ===========================================================================

  /**
   * Émis par `GET /v1/lookup/companies` quand au moins un candidat est
   * retourné ET tous les candidats proviennent uniquement du canal
   * trigram (`legal_name_trgm`) — aucun exact-match. Parité avec
   * `LOOKUP_COMPANIES_LOW_CONFIDENCE_LEGAL_NAME_ONLY` (peut coexister).
   */
  LOOKUP_COMPANIES_FUZZY_ONLY_LOW_CONFIDENCE: {
    kind: "warning",
    messages: {
      en: "All candidates matched only via fuzzy (trigram) similarity on legal_name — no exact match. Low confidence. Confirm company identity via a discriminating channel (neq, postal_code, phone, email) before any action.",
      fr: "Tous les candidats correspondent uniquement par similarité floue (trigramme) sur legal_name — aucun match exact. Confidence faible. Confirmer l'identité de la compagnie via un canal discriminant (neq, postal_code, phone, email) avant toute action.",
    },
  },

  /**
   * Émis par `GET /v1/lookup/projects` quand au moins un candidat est
   * retourné ET tous les candidats proviennent uniquement du canal
   * trigram (`title_trgm`) — aucun exact-match. Note : le court-circuit
   * `reference_code_exact` reste prioritaire et ne déclenche jamais ce
   * warning (return en haut du handler).
   */
  LOOKUP_PROJECTS_FUZZY_ONLY_LOW_CONFIDENCE: {
    kind: "warning",
    messages: {
      en: "All candidates matched only via fuzzy (trigram) similarity on title — no exact match. Low confidence. Confirm project identity via a discriminating channel (reference_code, company_id, effect_date) before any action.",
      fr: "Tous les candidats correspondent uniquement par similarité floue (trigramme) sur title — aucun match exact. Confidence faible. Confirmer l'identité du projet via un canal discriminant (reference_code, company_id, effect_date) avant toute action.",
    },
  },

  /**
   * Émis par `GET /v1/lookup/contacts` quand au moins un candidat est
   * retourné ET tous les candidats proviennent uniquement du canal
   * trigram (`contact_full_name_trgm`) — aucun exact-match.
   */
  LOOKUP_CONTACTS_FUZZY_ONLY_LOW_CONFIDENCE: {
    kind: "warning",
    messages: {
      en: "All candidates matched only via fuzzy (trigram) similarity on full_name — no exact match. Low confidence. Confirm contact identity via a discriminating channel (email, phone) before any action.",
      fr: "Tous les candidats correspondent uniquement par similarité floue (trigramme) sur full_name — aucun match exact. Confidence faible. Confirmer l'identité du contact via un canal discriminant (email, phone) avant toute action.",
    },
  },

  // ===========================================================================
  // Chunk 9.K.5 — Codes globaux émis par le handler global API
  // (`apps/api/src/middleware/error-handler.ts`). D58.6.
  //
  // Fermeture de la dernière surface FR-only côté API : les chemins génériques
  // ZodError / Postgres unique-violation / FK / NOT NULL / RLS / fallback 500
  // étaient encore en FR strict. Migrés en bilingue ADR 0027 P-3 pour parité
  // stricte avec les codes spécifiques 9.K.1 / 9.K.2.
  //
  // Convention : préfixe absent (codes transverses au système, pas liés à
  // une entité métier — parité conceptuelle avec PG SQLSTATE / RFC 7807).
  // ===========================================================================

  /**
   * Émis par le handler global pour toute `ZodError` non interceptée par
   * un schéma local (zValidator throw avant le handler global = belt &
   * braces). Aussi émis par `ApiErrors.validation()` legacy. 422.
   *
   * `details` porte les `issues` Zod ou `field_path` côté call-site —
   * non traduit (PO-Q4 9.K.5 : machine-data).
   */
  TENANTS_LOGO_INVALID: {
    kind: "error",
    messages: {
      en: "Choose a valid non-animated PNG or JPEG logo up to 1 MB and 4 million pixels.",
      fr: "Choisissez un logo PNG non animé ou JPEG valide de 1 Mo et 4 millions de pixels maximum.",
    },
  },
  VALIDATION_ERROR: {
    kind: "error",
    messages: {
      en: "Invalid input.",
      fr: "Données invalides.",
    },
  },

  /**
   * Émis par le handler global sur unique-violation Postgres (SQLSTATE
   * 23505). 409. `details` porte `err.detail` Postgres tel quel — non
   * traduit (PO-Q4).
   */
  CONFLICT_UNIQUE: {
    kind: "error",
    messages: {
      en: "A similar resource already exists.",
      fr: "Une ressource similaire existe déjà.",
    },
  },

  /**
   * Émis par le handler global sur foreign-key violation Postgres
   * (SQLSTATE 23503). 422. `details` porte `err.detail` Postgres.
   */
  FK_VIOLATION: {
    kind: "error",
    messages: {
      en: "Invalid reference to another resource.",
      fr: "Référence invalide vers une autre ressource.",
    },
  },

  /**
   * Émis par le handler global sur not-null violation Postgres (SQLSTATE
   * 23502). 422. `details` porte `err.detail` Postgres.
   */
  NOT_NULL_VIOLATION: {
    kind: "error",
    messages: {
      en: "Required field is missing.",
      fr: "Champ obligatoire manquant.",
    },
  },

  /**
   * Émis par le handler global sur insufficient-privilege Postgres
   * (SQLSTATE 42501) ou erreur PostgREST `PGRST*`. 403. PO-Q3 9.K.5 :
   * un seul code couvre les deux variantes — sémantique métier identique
   * côté client (« accès refusé par politique RLS »).
   */
  RLS_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Access denied by row-level security policy.",
      fr: "Accès refusé par la politique RLS.",
    },
  },

  /**
   * Émis par le handler global en fallback (chemin 4 : `Unhandled error`).
   * 500. PO-Q2 9.K.5 : wording figé. `details` reste vide en prod ; en
   * `isDev` seulement, `details.dev_message` peut porter `String(err)` brut
   * non traduit (machine-data, audience dev/agent IA — pas de fuite stack
   * en prod).
   */
  INTERNAL_ERROR: {
    kind: "error",
    messages: {
      en: "Internal server error.",
      fr: "Erreur serveur.",
    },
  },

  // ===========================================================================
  // Chunk 9.H.5 — Governance primitives (D11 minimum Couche 2 — gating)
  //
  // Émis par le middleware applicatif `assertHumanGated(c, action_key)` dans
  // `apps/api/src/lib/gating.ts` quand l'auth courante est une api_key et
  // que la policy active (tenant-spec ou globale par fallback) a
  // `agent_allowed = false`. 403.
  //
  // Anti-leak strict (PO-Q7) : `details` expose `action_key` (machine-data
  // utile à l'agent IA pour comprendre quelle action est gated) et
  // `policy_id` (référence audit traceable). N'expose PAS `requires_human` /
  // `agent_allowed` flags eux-mêmes (l'agent IA n'a pas à connaître l'inverse
  // logique de la policy pour explorer un contournement).
  //
  // Audience : agent IA externe + dev qui debug. Bilingue EN+FR self-contained.
  // Côté audit : `app.audit_log.gating_decision` capture `outcome='blocked'`
  // + `policy_id` + `action_key` pour traçabilité forensic complète.
  // ===========================================================================

  /**
   * Émis par `assertHumanGated()` quand une api_key tente d'invoquer une
   * action dont la policy active interdit l'exécution agent (`agent_allowed=false`).
   * 403. `details.action_key` (machine-data agent) + `details.policy_id`
   * (référence audit). PAS de `requires_human`/`agent_allowed` flags exposés
   * (PO-Q7 anti-leak inverse logique).
   *
   * Phase 1 9.H.5 — Couche 2 minimum (block/allow simple) ; pas de création
   * de tâche `awaiting_approval` (dépend de D34). User JWT bypass : ne lève
   * jamais ce code car `auth.kind === 'user' → ALLOW` (human IS the gate).
   */
  GATING_HUMAN_REQUIRED: {
    kind: "error",
    messages: {
      en: "This action requires human supervision. Agents are not permitted to call it for this tenant.",
      fr: "Cette action requiert une supervision humaine. Les agents ne sont pas autorisés à l'invoquer pour ce tenant.",
    },
  },

  // ===========================================================================
  // Chunk 9.K.6 — Codes CRUD companies / contacts / projects (D58.1)
  //
  // Fermeture des dernières surfaces FR-only côté handlers REST CRUD :
  // `apps/api/src/routes/companies.ts` et `apps/api/src/routes/projects.ts`
  // levaient `ApiErrors.notFound("Entreprise introuvable")` /
  // `ApiErrors.badRequest("Aucun champ à mettre à jour")` etc. — wrappés
  // best-effort par le handler global en `messages: { en, fr }` identiques
  // (FR seulement). Migration en codes dédiés ADR 0027 P-3 pour parité
  // stricte avec 9.K.1 / 9.K.2 / 9.K.5.
  //
  // Convention 9.K.6 : préfixe par entité (PO-Q3 9.K.1) — `COMPANIES_*`,
  // `CONTACTS_*`, `PROJECTS_*`. Granularité PO-Q2 : un code par cas distinct
  // (pas de `NOT_FOUND` générique partagé entre 3 entités). Les `details`
  // restent machine-data non traduite (UUID `resource_id` typiquement).
  // ===========================================================================

  /**
   * Émis par GET/PATCH/DELETE `/v1/companies/:id` quand l'entreprise est
   * introuvable (id inexistant ou soft-deleted). 404. `details.resource_id`
   * porte l'UUID demandé.
   */
  COMPANIES_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Company not found.",
      fr: "Entreprise introuvable.",
    },
  },

  /**
   * Émis par PATCH `/v1/companies/:id` quand le payload nettoyé (post
   * `undefined` filter) est vide. 400. Parité PO-Q2 avec
   * `CONTACTS_NO_FIELDS_TO_UPDATE` et `PROJECTS_NO_FIELDS_TO_UPDATE` —
   * code dédié par entité plutôt qu'un code transverse partagé.
   */
  COMPANIES_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },

  /**
   * 9.T.2 — Émis par `GET /v1/companies/lookup` quand aucun des 3 critères
   * minimum (`legal_name`, `postal_code`, `business_number`) n'est fourni.
   * 422. Wording neutre côté broker (l'endpoint exige au moins un filtre
   * discriminant pour éviter de retourner tout le tenant en mode browsing).
   */
  COMPANIES_LOOKUP_QUERY_REQUIRED: {
    kind: "error",
    messages: {
      en: "At least one of legal_name, postal_code, or business_number is required.",
      fr: "Au moins un des paramètres legal_name, postal_code ou business_number est requis.",
    },
  },

  /**
   * 9.T.2 — Émis par `GET /v1/companies/lookup` quand `limit` est hors
   * bornes (1..20). 422. Borne supérieure 20 = endpoint lookup ciblé,
   * pas browsing paginé.
   */
  COMPANIES_LOOKUP_LIMIT_INVALID: {
    kind: "error",
    messages: {
      en: "Limit must be between 1 and 20.",
      fr: "La limite doit être entre 1 et 20.",
    },
  },

  /**
   * Émis par PATCH/DELETE `/v1/contacts/:id` quand le contact est
   * introuvable (id inexistant ou soft-deleted). 404. `details.resource_id`
   * porte l'UUID demandé.
   */
  CONTACTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Contact not found.",
      fr: "Contact introuvable.",
    },
  },

  /**
   * Émis par PATCH `/v1/contacts/:id` quand le payload nettoyé est vide.
   * 400. Parité COMPANIES_NO_FIELDS_TO_UPDATE.
   */
  CONTACTS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },

  /**
   * Émis par GET/PATCH/DELETE `/v1/projects/:id` et
   * GET `/v1/projects/by-reference/:code` quand le projet est introuvable
   * (id ou reference_code inexistant, ou soft-deleted). 404.
   * `details.resource_id` ou `details.reference_code` porte la valeur
   * demandée selon la voie d'accès.
   */
  PROJECTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Project not found.",
      fr: "Projet introuvable.",
    },
  },

  /**
   * Émis par PATCH `/v1/projects/:id` quand le payload nettoyé est vide.
   * 400. Parité COMPANIES_NO_FIELDS_TO_UPDATE.
   */
  PROJECTS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },

  /**
   * Émis par GET `/v1/projects/by-reference/:code` quand le format de
   * `reference_code` ne correspond pas au pattern `PRJ-YYYY-NNNNNN`
   * (migration 0023). 400. `details.reference_code` porte la valeur reçue.
   *
   * Note : ce code est distinct de `VALIDATION_ERROR` (Zod) car le check
   * se fait dans le handler après la validation Zod du path param, sur un
   * regex métier spécifique (ADR 0023 reference_code).
   */
  PROJECTS_REFERENCE_CODE_INVALID_FORMAT: {
    kind: "error",
    messages: {
      en: "Invalid reference_code format. Expected: PRJ-YYYY-NNNNNN.",
      fr: "Format reference_code invalide. Attendu : PRJ-YYYY-NNNNNN.",
    },
  },

  /**
   * Émis par POST `/v1/projects` quand `company_id` du payload référence
   * une entreprise inexistante (ou soft-deleted). 404.
   * `details.company_id` porte l'UUID. Distinct de `COMPANIES_NOT_FOUND`
   * pour signaler que la cause est un FK upstream du POST projects, pas
   * une lecture directe d'entreprise.
   */
  PROJECTS_COMPANY_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Referenced company not found.",
      fr: "Entreprise référencée introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/projects` quand `company_id` existe mais appartient
   * à un autre tenant. 403. Belt & braces vs RLS (PostgREST renverrait un
   * 403 RLS sinon — ici on intercepte en amont pour un message dédié).
   */
  PROJECTS_COMPANY_FORBIDDEN_TENANT: {
    kind: "error",
    messages: {
      en: "Referenced company belongs to another tenant.",
      fr: "Entreprise référencée hors tenant.",
    },
  },

  // ===========================================================================
  // Chunk 9.K.7 — Codes CRUD api_keys / brokers / anomalies (D58.1 batch 2)
  //
  // Migration des routes `apps/api/src/routes/api-keys.ts` (13 sites legacy),
  // `apps/api/src/routes/brokers.ts` (11 sites legacy) et
  // `apps/api/src/routes/anomalies.ts` (5 sites legacy) — toutes en
  // `ApiErrors.* (msg FR strict)` → bilingue ADR 0027 P-3 via
  // `bilingualHttpError(code, status, details?)`.
  //
  // Convention 9.K.7 : préfixe par entité (PO-Q3 9.K.1) — `API_KEYS_*`,
  // `BROKERS_*`, `ANOMALIES_*`. Granularité PO-Q2 : un code par cas distinct.
  // Décisions PO 9.K.7 :
  //   - PO-Q1 : `BROKERS_ADMIN_ONLY` unique (le contexte HTTP suffit).
  //   - PO-Q2 : `BROKERS_MODIFY_SELF_OR_ADMIN_ONLY` distinct (règle métier
  //     différente : admin OU propriétaire).
  //   - PO-Q3 : anomalies forbidden tenant séparés par cible
  //     (`SUBMISSION` / `PROJECT`).
  //   - PO-Q4 : `BROKERS_INVITATION_FAILED` reçoit `details.upstream_message`
  //     non traduit (machine-data Supabase).
  //   - PO-Q5 : `API_KEYS_BUNDLE_UNKNOWN` porte `details.bundle_slug`.
  // ===========================================================================

  // --- API_KEYS (7 codes) ----------------------------------------------------

  /**
   * Émis par GET/POST/POST(revoke) `/v1/api-keys/:id` quand la clé est
   * introuvable. 404. `details.resource_id` porte l'UUID demandé.
   */
  API_KEYS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "API key not found.",
      fr: "API key introuvable.",
    },
  },

  /**
   * Émis par `ensureAdmin` quand l'auth n'est pas un utilisateur (ex.
   * accès tenté avec une api_key vers la gestion des api_keys). 403.
   * Distinct de `API_KEYS_FORBIDDEN_ADMIN_ONLY` : ici on bloque parce
   * qu'on exige un humain authentifié, pas un rôle particulier.
   */
  API_KEYS_FORBIDDEN_USER_ONLY: {
    kind: "error",
    messages: {
      en: "Reserved to authenticated user administrators.",
      fr: "Réservé aux administrateurs (utilisateur authentifié requis).",
    },
  },

  /**
   * Émis par `ensureAdmin` quand l'utilisateur n'a pas le rôle
   * `tenant_admin` ou `super_admin`. 403.
   */
  API_KEYS_FORBIDDEN_ADMIN_ONLY: {
    kind: "error",
    messages: {
      en: "Reserved to tenant admins or super admins.",
      fr: "Réservé aux administrateurs tenant ou super_admin.",
    },
  },

  /**
   * Émis par GET `/v1/api-keys` ou `/v1/api-keys/:id` ou
   * POST `/:id/revoke` quand le tenant n'est pas résolu (header
   * `x-tenant-id` manquant pour super_admin, ou tenant non rattaché pour
   * tenant_admin). 400.
   *
   * Note : couvre les 4 sites legacy distincts (POST création super_admin
   * sans tenant, POST création tenant_admin sans tenant, GET listing,
   * GET détail, POST revoke) — message variant adapté côté `details`
   * (`details.context` optionnel). Granularité PO-Q2 respectée car la
   * cause est UNE : tenant non résolu.
   */
  API_KEYS_TENANT_REQUIRED: {
    kind: "error",
    messages: {
      en: "Tenant is required (header `x-tenant-id` for super_admin).",
      fr: "Tenant requis (header `x-tenant-id` pour super_admin).",
    },
  },

  /**
   * Émis par POST `/v1/api-keys` quand un `tenant_admin` tente de créer
   * une clé pour un tenant ≠ son propre tenant. 403.
   */
  API_KEYS_TENANT_MISMATCH: {
    kind: "error",
    messages: {
      en: "Tenant admin can only create API keys for their own tenant.",
      fr: "tenant_admin ne peut créer une clé que pour son propre tenant.",
    },
  },

  /**
   * Émis par POST `/v1/api-keys` quand le `bundle` du payload n'est pas
   * un bundle reconnu (`mcp:read`, `mcp:write`, ...). 400. `details.bundle_slug`
   * porte la valeur reçue (PO-Q5 9.K.7).
   *
   * Note : Zod valide le format mais pas l'existence côté
   * `API_KEY_BUNDLE_TO_SCOPES` (registre runtime). Ce code est levé après
   * Zod, sur un check d'existence runtime.
   */
  API_KEYS_BUNDLE_UNKNOWN: {
    kind: "error",
    messages: {
      en: "Unknown API key bundle.",
      fr: "Bundle API key inconnu.",
    },
  },

  /**
   * Émis par POST `/v1/api-keys/:id/revoke` quand la clé est déjà révoquée
   * (`revoked_at IS NOT NULL`). 409.
   */
  API_KEYS_ALREADY_REVOKED: {
    kind: "error",
    messages: {
      en: "API key is already revoked.",
      fr: "Clé déjà révoquée.",
    },
  },

  /**
   * Émis par POST `/v1/api-keys` quand l'insertion DB retourne 0 ligne sans
   * lever d'erreur PG (cas système rare — DB silencieuse). 500. Distinct de
   * `INTERNAL_ERROR` 9.K.5 pour préserver la sémantique métier (création
   * api_key spécifiquement échouée, pas n'importe quel chemin serveur).
   */
  API_KEYS_INSERT_FAILED: {
    kind: "error",
    messages: {
      en: "API key insertion failed.",
      fr: "Insertion de la clé API échouée.",
    },
  },

  // --- BROKERS (7 codes) -----------------------------------------------------

  /**
   * Émis par GET/PATCH/DELETE `/v1/brokers/:id` et PATCH `/v1/brokers/:id/role`
   * quand le courtier est introuvable (id inexistant ou soft-deleted). 404.
   */
  BROKERS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Broker not found.",
      fr: "Courtier introuvable.",
    },
  },

  /**
   * Émis par les routes admin-only `/v1/brokers` (POST invite, PATCH role,
   * DELETE) quand l'appelant n'a pas le rôle admin. 403. PO-Q1 9.K.7 :
   * code unique pour les cas admin-only généraux ; le contexte HTTP +
   * `details.action` (`"invite" | "update_role" | "delete"`) suffit pour
   * désambiguïser.
   */
  BROKERS_ADMIN_ONLY: {
    kind: "error",
    messages: {
      en: "Reserved to administrators.",
      fr: "Réservé aux administrateurs.",
    },
  },

  /**
   * Émis par PATCH `/v1/brokers/:id` quand l'appelant n'est ni admin ni
   * propriétaire du profil (cas self-service). 403. PO-Q2 9.K.7 : DISTINCT
   * de `BROKERS_ADMIN_ONLY` car la règle métier autorise le propriétaire
   * en plus des admins (pas le même pattern d'autorisation).
   */
  BROKERS_MODIFY_SELF_OR_ADMIN_ONLY: {
    kind: "error",
    messages: {
      en: "Modification reserved to administrators or to the profile owner.",
      fr: "Modification réservée à l'admin ou au propriétaire.",
    },
  },

  /**
   * Émis par POST `/v1/brokers/invite` quand l'appel Supabase Auth
   * `inviteUserByEmail` échoue. 400. PO-Q4 9.K.7 :
   * `details.upstream_message` porte le message Supabase brut (anglais,
   * non traduit — machine-data / dev-data destinée aux logs).
   */
  BROKERS_INVITATION_FAILED: {
    kind: "error",
    messages: {
      en: "Broker invitation failed.",
      fr: "Invitation impossible.",
    },
  },

  /**
   * Émis par PATCH `/v1/brokers/:id` quand le payload nettoyé (post
   * `undefined` filter) est vide. 400. Parité 9.K.6
   * `COMPANIES_NO_FIELDS_TO_UPDATE`.
   */
  BROKERS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },

  /**
   * Émis par PATCH `/v1/brokers/:id/role` quand le payload nettoyé est
   * vide (ni `role` ni `status`). 400. Distinct de
   * `BROKERS_NO_FIELDS_TO_UPDATE` car la règle est plus stricte ici :
   * exactement l'un des deux champs admin doit être présent.
   */
  BROKERS_ROLE_OR_STATUS_REQUIRED: {
    kind: "error",
    messages: {
      en: "Field `role` or `status` is required.",
      fr: "Champ role ou status requis.",
    },
  },

  /**
   * Émis par DELETE `/v1/brokers/:id` quand l'appelant tente de
   * supprimer son propre compte. 400. Garde-fou métier (auto-éviction
   * non permise — empêche le tenant de perdre son dernier admin par
   * erreur).
   */
  BROKERS_CANNOT_DELETE_SELF: {
    kind: "error",
    messages: {
      en: "Cannot delete your own account.",
      fr: "Impossible de supprimer son propre compte.",
    },
  },

  // --- ANOMALIES (3 codes) ---------------------------------------------------

  /**
   * Émis par GET/PATCH `/v1/anomalies/:id` (resolve / reopen) quand
   * l'anomalie est introuvable, déjà résolue (sur `resolve`), ou non
   * résolue (sur `reopen`). 404. `details.context` optionnel
   * (`"already_resolved" | "not_resolved"`) porte la nuance — le code
   * canonique reste unique (PO-Q2 : la cause externe est la même =
   * "ressource indisponible pour cette transition").
   */
  ANOMALIES_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Anomaly not found.",
      fr: "Anomalie introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/anomalies` quand ni `submission_id` ni
   * `project_id` n'est fourni. 400. Au moins une cible discriminante
   * requise.
   */
  ANOMALIES_TARGET_REQUIRED: {
    kind: "error",
    messages: {
      en: "Either submission_id or project_id is required.",
      fr: "submission_id ou project_id requis.",
    },
  },

  /**
   * Émis par POST `/v1/anomalies` quand `submission_id` référence une
   * soumission appartenant à un autre tenant. 403. PO-Q3 9.K.7 : DISTINCT
   * de `ANOMALIES_PROJECT_FORBIDDEN_TENANT` (cible distincte).
   * `details.resource_id` porte l'UUID submission.
   */
  ANOMALIES_SUBMISSION_FORBIDDEN_TENANT: {
    kind: "error",
    messages: {
      en: "Referenced submission belongs to another tenant.",
      fr: "Soumission hors tenant.",
    },
  },

  /**
   * Émis par POST `/v1/anomalies` quand `project_id` référence un projet
   * appartenant à un autre tenant. 403. PO-Q3 9.K.7 : DISTINCT de
   * `ANOMALIES_SUBMISSION_FORBIDDEN_TENANT`. `details.resource_id` porte
   * l'UUID project.
   */
  ANOMALIES_PROJECT_FORBIDDEN_TENANT: {
    kind: "error",
    messages: {
      en: "Referenced project belongs to another tenant.",
      fr: "Projet hors tenant.",
    },
  },

  // ===========================================================================
  // Chunk 9.K.8 — Codes CRUD retention / legal-holds (D58.1 batch 3)
  //
  // Migration des routes `apps/api/src/routes/retention.ts` (14 sites legacy)
  // et `apps/api/src/routes/legal-holds.ts` (8 sites legacy) — toutes en
  // `ApiErrors.* (msg FR strict)` → bilingue ADR 0027 P-3 via
  // `bilingualHttpError(code, status, details?)`.
  //
  // Convention 9.K.8 : préfixe par entité (PO-Q3 9.K.1) — `RETENTION_*`,
  // `LEGAL_HOLDS_*`. Granularité PO-Q2 : un code par cas distinct.
  //
  // Décisions PO finales 9.K.8 (post-revue) :
  //   - `LEGAL_HOLDS_SCOPE_KIND_UNSUPPORTED` (et non `RETENTION_*`) :
  //     levé physiquement et exclusivement depuis `legal-holds.ts` sur un
  //     POST client invalide. Préfixe `LEGAL_HOLDS_*` reflète la route
  //     d'origine et l'audience de l'erreur.
  //   - PAS de `LEGAL_HOLDS_RELEASE_NOT_FOUND` séparé : `LEGAL_HOLDS_NOT_FOUND`
  //     suffit, le flow release ajoute `details.context: "release"` (parité
  //     avec PO-Q3 9.K.7 sur `ANOMALIES_NOT_FOUND` mono-code + `details`).
  // ===========================================================================

  // --- RETENTION (10 codes) --------------------------------------------------

  /**
   * Émis par GET `/v1/data-class-registry/:table_name/:column_name` quand
   * la classification (table_name, column_name) est introuvable. 404.
   * `details.table_name` et `details.column_name` portent les valeurs
   * demandées.
   */
  RETENTION_DATA_CLASS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Data classification not found.",
      fr: "Classification introuvable.",
    },
  },

  /**
   * Émis par GET/PATCH `/v1/retention-policies/:id` quand la policy est
   * introuvable (id inexistant ou hors scope tenant). 404.
   * `details.resource_id` porte l'UUID demandé.
   */
  RETENTION_POLICY_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Retention policy not found.",
      fr: "Retention policy introuvable.",
    },
  },

  /**
   * Émis par POST/PATCH `/v1/retention-policies` quand un `tenant_admin`
   * tente de créer/modifier une policy globale (`tenant_id IS NULL`).
   * 403. Réservé super_admin (PO-Q2/Q4 9.H.3).
   */
  RETENTION_POLICY_GLOBAL_SUPER_ADMIN_ONLY: {
    kind: "error",
    messages: {
      en: "Only super_admin can manage global retention policies (tenant_id null).",
      fr: "Seul un super_admin peut gérer les retention policies globales (tenant_id null).",
    },
  },

  /**
   * Émis par POST/PATCH `/v1/retention-policies` quand un `tenant_admin`
   * tente de créer/modifier une policy d'un autre tenant. 403.
   * `details.target_tenant_id` porte le tenant_id du payload (POST) ou
   * de la row courante (PATCH).
   */
  RETENTION_POLICY_TENANT_MISMATCH: {
    kind: "error",
    messages: {
      en: "Tenant admin can only manage retention policies for their own tenant.",
      fr: "tenant_admin ne peut gérer une policy que pour son propre tenant.",
    },
  },

  /**
   * Émis par POST/PATCH `/v1/retention-policies` sur SQLSTATE 23514
   * (CHECK constraint violation Postgres). 400. `details.constraint`
   * porte le nom de la contrainte (machine-data non traduite).
   */
  RETENTION_POLICY_CHECK_VIOLATION: {
    kind: "error",
    messages: {
      en: "Retention policy integrity constraint violated.",
      fr: "Contrainte d'intégrité retention policy violée.",
    },
  },

  /**
   * Émis par POST `/v1/retention-policies` sur SQLSTATE 23505
   * (`unique_retention_policy_partial` — clash sur tuple
   * `(tenant_id, data_category, module)`). 409. `details.tenant_id`,
   * `details.data_category`, `details.module` portent le tuple en clash.
   */
  RETENTION_POLICY_DUPLICATE: {
    kind: "error",
    messages: {
      en: "A retention policy already exists for this (tenant_id, data_category, module).",
      fr: "Une policy existe déjà pour ce (tenant_id, data_category, module).",
    },
  },

  /**
   * Émis par PATCH `/v1/retention-policies/:id` quand un `tenant_admin`
   * tente de modifier `legal_minimum_days` (réservé super_admin —
   * PO-Q4 9.H.3). 403.
   */
  RETENTION_POLICY_LEGAL_MINIMUM_DAYS_IMMUTABLE: {
    kind: "error",
    messages: {
      en: "tenant_admin cannot modify legal_minimum_days (reserved to super_admin).",
      fr: "tenant_admin ne peut pas modifier legal_minimum_days (réservé super_admin).",
    },
  },

  /**
   * Émis par PATCH `/v1/retention-policies/:id` quand le futur état
   * (computed: `input.legal_minimum_days ?? cur.legal_minimum_days` >
   * `input.retention_days ?? cur.retention_days`) violerait l'invariant
   * `legal_minimum_days ≤ retention_days`. 400. Distinct de
   * `RETENTION_POLICY_CHECK_VIOLATION` (DB) car le check applicatif est
   * fait AVANT l'UPDATE (message dédié + erreur claire).
   */
  RETENTION_POLICY_LEGAL_MINIMUM_EXCEEDS_RETENTION: {
    kind: "error",
    messages: {
      en: "legal_minimum_days cannot exceed retention_days (legal floor inconsistent).",
      fr: "legal_minimum_days ne peut pas excéder retention_days (plancher légal incohérent).",
    },
  },

  /**
   * Émis par PATCH `/v1/retention-policies/:id` quand le body ne
   * fournit aucun champ éditable (tous les champs sont `undefined`).
   * 400. `details.field` vaut `"_body"`. Migration 9.K.12 (D58.1-b
   * refines schemas) du `.refine()` Zod intra-schema vers handler
   * bilingue ADR 0027 (P-3). Empêche un PATCH no-op qui produirait
   * néanmoins une entrée d'audit vide (cf. PO-Q1 9.H.3).
   */
  RETENTION_POLICY_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "At least one field must be provided for PATCH.",
      fr: "Au moins un champ doit être fourni pour PATCH.",
    },
  },

  /**
   * Émis par GET `/v1/retention-audit-log` quand zéro filtre + zéro
   * fenêtre temporelle fournis. 400. Pattern F-01 (anti-scan complet).
   * Parité conceptuelle avec `LOOKUP_*_FILTER_REQUIRED` (9.K.1/9.K.2).
   */
  RETENTION_AUDIT_LOG_FILTER_REQUIRED: {
    kind: "error",
    messages: {
      en: "At least one filter is required: action, target_kind, target_id, policy_id, legal_hold_id, from, or to.",
      fr: "Au moins un filtre requis : action, target_kind, target_id, policy_id, legal_hold_id, from ou to.",
    },
  },

  /**
   * Émis par GET `/v1/retention-audit-log/:id` quand l'entrée d'audit
   * est introuvable. 404. `details.resource_id` porte l'UUID demandé.
   */
  RETENTION_AUDIT_LOG_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Retention audit log entry not found.",
      fr: "Entrée d'audit retention introuvable.",
    },
  },

  // --- LEGAL_HOLDS (6 codes) -------------------------------------------------

  /**
   * Émis par POST `/v1/legal-holds` quand `scope_kind` est dans l'enum
   * DB mais non supporté côté handler 9.H.3 (ex. `employee_census` :
   * table inexistante). 400. `details.scope_kind` porte la valeur
   * reçue. Préfixe `LEGAL_HOLDS_*` car le code est levé exclusivement
   * depuis `legal-holds.ts` sur un POST client invalide (cf.
   * SCOPE_KIND_TABLE `__UNSUPPORTED__`).
   */
  LEGAL_HOLDS_SCOPE_KIND_UNSUPPORTED: {
    kind: "error",
    messages: {
      en: "scope_kind not supported in 9.H.3 (target table does not exist).",
      fr: "scope_kind non supporté en 9.H.3 (table inexistante).",
    },
  },

  /**
   * Émis par POST `/v1/legal-holds` quand `scope_id` ne référence aucune
   * row dans la table cible (`SCOPE_KIND_TABLE[scope_kind]`) pour ce
   * tenant. 400. `details.scope_kind`, `details.scope_id`,
   * `details.target_table` portent le contexte.
   *
   * Distinct de `LEGAL_HOLDS_NOT_FOUND` (404 sur le hold lui-même) car
   * la cause est un FK upstream du POST (la ressource cible n'existe
   * pas), pas un 404 sur le hold.
   */
  LEGAL_HOLDS_SCOPE_REF_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "scope_id not found in target table for this tenant.",
      fr: "scope_id introuvable dans la table cible pour ce tenant.",
    },
  },

  /**
   * Émis par GET `/v1/legal-holds/active` quand le tenant n'est pas
   * résolu (header `x-tenant-id` manquant). 400. Parité conceptuelle
   * avec `API_KEYS_TENANT_REQUIRED` (9.K.7).
   */
  LEGAL_HOLDS_TENANT_REQUIRED: {
    kind: "error",
    messages: {
      en: "Tenant is required for this check (header `x-tenant-id`).",
      fr: "Tenant requis pour ce check (header `x-tenant-id`).",
    },
  },

  /**
   * Émis par GET `/v1/legal-holds/:id` (lecture directe) ET par
   * PATCH `/v1/legal-holds/:id/release` (mutation release) quand le
   * hold est introuvable (id inexistant ou hors scope tenant). 404.
   * `details.resource_id` porte l'UUID demandé. Le call-site PATCH
   * release ajoute `details.context: "release"` pour disambiguer le
   * flow côté agent IA / UX (parité avec `ANOMALIES_NOT_FOUND` 9.K.7
   * + `details.context`).
   */
  LEGAL_HOLDS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Legal hold not found.",
      fr: "Legal hold introuvable.",
    },
  },

  /**
   * Émis par POST/PATCH `/v1/legal-holds` sur SQLSTATE 23514
   * (CHECK constraint Postgres, e.g. `legal_holds_scope_id_consistency_chk`).
   * 400. `details.constraint` porte le nom de la contrainte
   * (machine-data non traduite).
   */
  LEGAL_HOLDS_CHECK_VIOLATION: {
    kind: "error",
    messages: {
      en: "Legal hold integrity constraint violated.",
      fr: "Contrainte d'intégrité legal hold violée.",
    },
  },

  /**
   * Émis par PATCH `/v1/legal-holds/:id/release` quand le hold n'est
   * pas en status `active` (déjà released ou autre). 409.
   * `details.current_status` porte le statut actuel,
   * `details.expected_status` = `"active"`.
   */
  LEGAL_HOLDS_RELEASE_INVALID_STATUS: {
    kind: "error",
    messages: {
      en: "Legal hold is not releasable (expected status 'active').",
      fr: "Legal hold non relâchable (statut attendu 'active').",
    },
  },

  /**
   * Émis par POST `/v1/legal-holds` quand la cohérence
   * `(scope_kind, scope_id)` est violée côté handler :
   *   - `scope_kind = 'tenant'` mais `scope_id` non null, OU
   *   - `scope_kind ≠ 'tenant'` mais `scope_id` absent / null.
   * 400. `details.scope_kind` porte la valeur reçue, `details.field`
   * vaut `"scope_id"`. Migration 9.K.12 (D58.1-b refines schemas) du
   * `superRefine` Zod intra-schema vers handler bilingue ADR 0027 (P-3).
   * Distinct de `LEGAL_HOLDS_CHECK_VIOLATION` (DB 23514) car le check
   * applicatif est fait AVANT l'INSERT, pour une erreur claire et
   * traduite ; la CHECK DB reste filet de sécurité (cas concurrents).
   */
  LEGAL_HOLDS_SCOPE_ID_INVALID: {
    kind: "error",
    messages: {
      en: "scope_id is inconsistent with scope_kind (must be null when scope_kind = 'tenant', required otherwise).",
      fr: "scope_id incohérent avec scope_kind (doit être null si scope_kind = 'tenant', requis sinon).",
    },
  },

  // ===========================================================================
  // Chunk 9.K.9 — Codes CRUD submissions / intake-events / missing-information /
  // document-links / quote-requests (D58.1 batch 4)
  //
  // Migration des routes :
  //   - `apps/api/src/routes/submissions.ts`         (6 sites legacy)
  //   - `apps/api/src/routes/intake-events.ts`       (1 site legacy)
  //   - `apps/api/src/routes/missing-information.ts` (1 site legacy)
  //   - `apps/api/src/routes/document-links.ts`      (2 sites legacy)
  //   - `apps/api/src/routes/quote-requests.ts`      (1 site legacy)
  // → 11 sites `ApiErrors.* (msg FR strict)` migrés bilingue ADR 0027 P-3
  // via `bilingualHttpError(code, status, details?)`.
  //
  // Convention 9.K.9 : préfixe par entité pluriel (PO-Q3 9.K.1 + PO-Q4 9.K.9) —
  // `SUBMISSIONS_*`, `INTAKE_EVENTS_*`, `MISSING_INFORMATION_*` (PO-Q3 9.K.9 :
  // préfixe long, parité table DB), `DOCUMENT_LINKS_*`, `QUOTE_REQUESTS_*`.
  // Granularité PO-Q2 : un code par cas distinct.
  //
  // Décisions PO 9.K.9 :
  //   - PO-Q1 : `SUBMISSIONS_PROJECT_*` distincts (parité 9.K.7 `ANOMALIES_PROJECT_*`
  //     + 9.K.6 `PROJECTS_COMPANY_*` — sémantique = "soumission qui référence
  //     un projet introuvable", pas "projet lu directement").
  //   - PO-Q2 : `DOCUMENT_LINKS_FILTER_REQUIRED` porte
  //     `details.allowed_filters: ["file_id","attachable_kind","attachable_id"]`.
  //   - PO-Q3 : `MISSING_INFORMATION_*` complet (pas `MISSING_INFO_*`) —
  //     parité avec nom de route et table DB `app.missing_information`.
  //   - PO-Q4 : `INTAKE_EVENTS_*` au pluriel (parité 9.K.7 `API_KEYS_*`,
  //     `BROKERS_*`, `ANOMALIES_*`).
  //   - PO-Q5 : `SUBMISSIONS_PROJECT_FORBIDDEN_TENANT` parité stricte
  //     `ANOMALIES_PROJECT_FORBIDDEN_TENANT` (9.K.7).
  // ===========================================================================

  // --- SUBMISSIONS (4 codes) -------------------------------------------------

  /**
   * Émis par GET/PATCH/DELETE `/v1/submissions/:id` quand la soumission
   * est introuvable (id inexistant ou soft-deleted). 404.
   * `details.resource_id` porte l'UUID demandé.
   */
  SUBMISSIONS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Submission not found.",
      fr: "Soumission introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/submissions` quand `project_id` du payload référence
   * un projet inexistant (ou soft-deleted). 404. `details.project_id` porte
   * l'UUID. PO-Q1 9.K.9 : DISTINCT de `PROJECTS_NOT_FOUND` (9.K.6) car la
   * cause est un FK upstream du POST submissions, pas une lecture directe
   * de projet (parité conceptuelle avec `PROJECTS_COMPANY_NOT_FOUND` 9.K.6
   * et `ANOMALIES_PROJECT_FORBIDDEN_TENANT` 9.K.7).
   */
  SUBMISSIONS_PROJECT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Referenced project not found.",
      fr: "Projet référencé introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/submissions` quand `project_id` existe mais
   * appartient à un autre tenant. 403. PO-Q5 9.K.9 : parité stricte
   * `ANOMALIES_PROJECT_FORBIDDEN_TENANT` (9.K.7). Belt & braces vs RLS
   * (PostgREST renverrait un 403 RLS sinon — ici on intercepte en amont
   * pour un message dédié et `details.resource_id`).
   */
  SUBMISSIONS_PROJECT_FORBIDDEN_TENANT: {
    kind: "error",
    messages: {
      en: "Referenced project belongs to another tenant.",
      fr: "Projet référencé hors tenant.",
    },
  },

  /**
   * Émis par PATCH `/v1/submissions/:id` quand le payload nettoyé (post
   * `undefined` filter) est vide. 400. Parité 9.K.6
   * `COMPANIES_NO_FIELDS_TO_UPDATE` / 9.K.7 `BROKERS_NO_FIELDS_TO_UPDATE` —
   * code dédié par entité plutôt qu'un code transverse partagé.
   */
  SUBMISSIONS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },

  // --- INTAKE_EVENTS (3 codes — 1 lecture + 2 9.G.1 transitions) -------------

  /**
   * Émis par GET `/v1/intake-events/:id` ET par PATCH `/:id/resolve` ou
   * `/:id/reject` (9.G.1) quand l'event est introuvable (id inexistant
   * ou hors scope tenant). 404. `details.resource_id` porte l'UUID
   * demandé. PO-Q4 9.K.9 : préfixe pluriel `INTAKE_EVENTS_*`
   * (parité 9.K.7 `API_KEYS_*`, `BROKERS_*`, `ANOMALIES_*`).
   */
  INTAKE_EVENTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Intake event not found.",
      fr: "Intake event introuvable.",
    },
  },

  /**
   * Émis par PATCH `/v1/intake-events/:id/resolve` ou `/:id/reject` (9.G.1)
   * quand le `status` actuel n'autorise pas la transition demandée. 409.
   *
   * Transitions autorisées Phase 1 :
   *   `pending_classification | pending_resolution → resolved`
   *   `pending_classification | pending_resolution → rejected`
   *
   * Tout autre status courant (`resolved`, `rejected`, `superseded`) =>
   * INVALID_TRANSITION. Couvre aussi les race conditions concurrency
   * (rowCount=0 sur l'UPDATE atomic — Q5 PO check status uniquement,
   * pas d'ETag/If-Match Phase 1).
   *
   * Pas de `details` projeté wire-side (anti-leak status interne, info
   * minimale au broker côté UX). Le broker peut re-fetch GET /:id pour
   * voir le status courant.
   */
  INTAKE_EVENTS_INVALID_TRANSITION: {
    kind: "error",
    messages: {
      en: "Intake event cannot transition from its current status.",
      fr: "L'intake event ne peut pas changer depuis son statut actuel.",
    },
  },

  /**
   * Émis par PATCH `/v1/intake-events/:id/resolve` (9.G.1) quand le
   * `target_id` (projet ou quote_request selon `resolution_kind`) est
   * introuvable OU appartient à un autre tenant. 404.
   *
   * **Anti-leak unique** : un seul code pour les 2 cas not_found vs
   * cross-tenant (parité conceptuelle `INTAKE_LINKS_NOT_FOUND` 9.N.1.A
   * qui couvre id inconnu + cross-tenant). Évite de signaler à un
   * broker l'existence d'une ressource d'un autre tenant.
   *
   * `details.resource_id` porte l'UUID `target_id` demandé.
   */
  INTAKE_EVENTS_RESOLVE_TARGET_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Resolve target (project or quote request) not found.",
      fr: "Cible de résolution (projet ou quote request) introuvable.",
    },
  },

  /**
   * 9.T.3 — Émis par `POST /v1/intake-events` quand le multipart broker
   * upload ne contient aucun fichier (clé `files` absente ou vide).
   * 400. Le broker doit attacher au moins 1 PDF — un intake_event sans
   * fichier n'a pas de raison d'être Phase 1.
   */
  INTAKE_EVENTS_UPLOAD_NO_FILES: {
    kind: "error",
    messages: {
      en: "At least one PDF file is required.",
      fr: "Au moins un fichier PDF est requis.",
    },
  },

  /**
   * 9.T.3 — Émis par `POST /v1/intake-events` quand le multipart broker
   * upload viole les contraintes de validation (count > 10, size > 25 MB
   * per file ou 100 MB total, MIME ≠ application/pdf, magic bytes invalides,
   * filename vide ou trop long). 400. `details.reason` discrimine le cas
   * spécifique pour l'UI broker.
   *
   * Convention parité `PUBLIC_INTAKE_LINK_UNAVAILABLE` 9.N.1.B (anti-leak
   * unique côté public) : ici le broker est authentifié, pas de risque
   * d'énumération — on peut être plus explicite via `details.reason`.
   */
  INTAKE_EVENTS_UPLOAD_INVALID_FILES: {
    kind: "error",
    messages: {
      en: "Uploaded files violate validation constraints (count, size, type, or filename).",
      fr: "Les fichiers téléversés violent les contraintes de validation (nombre, taille, type ou nom).",
    },
  },

  /**
   * Émis par `POST /v1/intake-events/:id/materialize` (Chunk 9.T.4) quand
   * la combinaison `strategy` × champs fournis est invalide :
   *   - `attach_existing_project` sans `existing_project_id`
   *   - `attach_company_create_project` sans `existing_company_id` OU sans `new_project`
   *   - `create_company_and_project` sans `new_company` OU sans `new_project`
   * 422. `details.reason` porte le motif structuré : `missing_existing_project_id`
   * / `missing_existing_company_id` / `missing_new_project` / `missing_new_company`.
   */
  INTAKE_EVENTS_MATERIALIZE_STRATEGY_INVALID: {
    kind: "error",
    messages: {
      en: "Materialize strategy is invalid — required fields are missing for the chosen strategy.",
      fr: "La stratégie de matérialisation est invalide — des champs requis sont manquants pour la stratégie choisie.",
    },
  },

  /**
   * Émis par `POST /v1/intake-events/:id/materialize` (Chunk 9.T.4) quand
   * `strategy` ∈ {`attach_company_create_project`, `create_company_and_project`}
   * mais `confirmed_create` n'est pas `true`. 422.
   *
   * Rationale PO Q3 : garde anti-double-clic + trace audit explicite « broker
   * a confirmé la création ». Non requis pour `attach_existing_project` qui
   * ne crée aucune entité.
   */
  INTAKE_EVENTS_MATERIALIZE_CONFIRM_REQUIRED: {
    kind: "error",
    messages: {
      en: "Confirmation is required to create a new company or project. Set confirmed_create=true.",
      fr: "La confirmation est requise pour créer une nouvelle company ou un nouveau projet. Fournir confirmed_create=true.",
    },
  },

  /**
   * Émis par `POST /v1/intake-events/:id/materialize` (Chunk 9.T.4 smoke fix)
   * quand `strategy === "create_company_and_project"` MAIS un doublon évident
   * (confidence >= 0.95 — business_number exact OU name+postal exact
   * normalisé) est détecté avant l'INSERT company.
   *
   * `confirmed_create=true` ne bypasse PAS cette détection : il confirme LA
   * CRÉATION voulue, pas « je sais qu'il y a un duplicate évident et je veux
   * quand même ». Le broker doit basculer vers `attach_company_create_project`
   * avec la company existante détectée.
   *
   * Wire response :
   *   status: 409
   *   details: { candidates: CompanyDuplicateCandidate[] }
   *     - candidates[i].company_id (UUID)
   *     - candidates[i].legal_name
   *     - candidates[i].business_number
   *     - candidates[i].address_city
   *     - candidates[i].address_postal
   *     - candidates[i].confidence (1.0 | 0.95)
   *     - candidates[i].match_kind ("business_number_exact" | "name_postal_exact")
   *
   * Anti-régression volontaire : matches trigram approximatifs (0.6-0.94)
   * NE déclenchent PAS cette erreur — le broker garde stratégie 3 open pour
   * les cas de nom générique / faux positif.
   */
  INTAKE_EVENTS_MATERIALIZE_DUPLICATE_COMPANY_DETECTED: {
    kind: "error",
    messages: {
      en: "A company with this exact business number or name+postal code already exists. Switch to Attach existing company + create project.",
      fr: "Une entreprise avec ce numéro d'entreprise ou ce nom + code postal exact existe déjà. Basculez vers Attacher client + créer projet.",
    },
  },

  /**
   * Émis par POST `/v1/companies` (9.T.4 post-smoke fix 3e itération) quand
   * une compagnie avec exactement le même business_number OU le même couple
   * legal_name + postal_code normalisés existe déjà pour le tenant courant.
   * 409.
   *
   * Détection symétrique à `INTAKE_EVENTS_MATERIALIZE_DUPLICATE_COMPANY_DETECTED`
   * — même helper `detectExactCompanyDuplicates`, même portée exact-only
   * (confidence >= 0.95). Pas de trigram approximatif — anti-faux-positifs.
   *
   * Réponse wire :
   *   details: { candidates: CompanyDuplicateCandidate[] }
   *     - candidates[i].company_id (UUID)
   *     - candidates[i].legal_name
   *     - candidates[i].business_number
   *     - candidates[i].address_city
   *     - candidates[i].address_postal
   *     - candidates[i].confidence (1.0 | 0.95)
   *     - candidates[i].match_kind ("business_number_exact" | "name_postal_exact")
   *
   * Contrairement au flow materialize, POST /v1/companies n'a **pas** de
   * champ `confirmed_create` — un duplicate exact bloque toujours. Le broker
   * doit soit corriger les champs, soit ouvrir la company existante.
   */
  COMPANIES_CREATE_DUPLICATE_DETECTED: {
    kind: "error",
    messages: {
      en: "A company with this exact business number or name+postal code already exists. Open the existing client instead of creating a duplicate.",
      fr: "Une entreprise avec ce numéro d'entreprise ou ce nom + code postal exact existe déjà. Ouvrez le client existant plutôt que de créer un doublon.",
    },
  },

  /**
   * Émis par POST `/v1/intake-events/:id/materialize` (9.T.4 post-smoke fix
   * 2e itération) quand la stratégie `attach_company_create_project`
   * tenterait de créer un `app.projects` dupliqué dans la même company.
   * 409.
   *
   * Détection portée volontairement limitée à un match `title` exact
   * (normalisé lower + collapse whitespace + btrim) parmi les projets
   * vivants (non `archived`, non soft-deleted) de la company cible. Pas
   * de trigram approximatif — anti-faux-positifs sur titres génériques.
   *
   * Réponse wire :
   *   details: { candidates: ProjectDuplicateCandidate[] }
   *     - candidates[i].project_id (UUID)
   *     - candidates[i].title
   *     - candidates[i].status (app.project_status, ex: draft, collecting…)
   *     - candidates[i].reference_code
   *     - candidates[i].created_at
   *     - candidates[i].confidence (1.0)
   *     - candidates[i].match_kind ("title_exact")
   *
   * Broker attendu bascule vers stratégie 1 (`attach_existing_project`)
   * avec le project candidat. `confirmed_create=true` NE bypasse PAS —
   * même sémantique que le duplicate company detected code.
   */
  INTAKE_EVENTS_MATERIALIZE_DUPLICATE_PROJECT_DETECTED: {
    kind: "error",
    messages: {
      en: "A project with this exact title already exists for this client. Switch to Attach to existing project.",
      fr: "Un projet avec ce titre exact existe déjà pour ce client. Basculez vers Attacher à un projet existant.",
    },
  },

  /**
   * 9.T.5.v3.5 — émis par `applyReviewedExtractionToCompanyAndProject` quand
   * le UPDATE app.projects ou app.companies retourne rowCount=0 (row not found
   * ou deleted_at IS NOT NULL) alors que le service est appelé APRÈS création /
   * attachement dans le workflow materialize enrichi. 422 = anomalie métier
   * post-Zod (l'invariant est que la row existe à cette étape). `details.reason`
   * précise `project_not_found_or_deleted` OR `company_not_found_or_deleted`.
   */
  INTAKE_EVENTS_MATERIALIZE_APPLY_EXTRACTION_FAILED: {
    kind: "error",
    messages: {
      en: "Applying extracted data failed — target row unavailable within the current transaction.",
      fr: "Application des données extraites échouée — la ligne cible est indisponible dans la transaction courante.",
    },
  },

  // --- INTAKE_EVENTS post-materialize actions (9.T.5.v2.2 — 4 codes) ---------
  //
  // 3 endpoints humain-only post-materialize (create-submission-from-file /
  // create-quote-request-from-file / attach-project-only) partagent 3 guards
  // et un duplicate guard pour submission. Anti-leak strict : aucun détail
  // technique wire-side (broker peut re-fetch GET /:id pour re-diagnostic).

  /**
   * Émis par les 3 endpoints post-materialize quand l'intake_event courant
   * n'est pas `status='resolved'`. 409. Couvre :
   *   - event en `pending_classification|pending_resolution` (materialize pas
   *     encore fait)
   *   - event en `rejected|superseded` (terminaison non-materialize)
   * Pas de `details` (broker consulte GET /:id pour voir le status courant).
   */
  INTAKE_EVENT_ACTION_INVALID_STATUS: {
    kind: "error",
    messages: {
      en: "This intake event must be resolved via materialize before this action.",
      fr: "Cet événement doit être matérialisé avant de pouvoir effectuer cette action.",
    },
  },

  /**
   * Émis par les 3 endpoints post-materialize quand l'intake_event a déjà
   * une direction commerciale non-`unknown` (anti-double-direction). 409.
   * Une fois qu'un event est directed vers insurer_response/client_input/
   * advisor_document, la switch retour est un flux distinct (hors scope
   * 9.T.5.v2.2 — dette D-NEW future si besoin métier).
   */
  INTAKE_EVENT_ACTION_ALREADY_DIRECTED: {
    kind: "error",
    messages: {
      en: "This intake event has already been directed. It cannot be reassigned.",
      fr: "Cet événement a déjà une direction assignée. Il ne peut plus être réorienté.",
    },
  },

  /**
   * Émis par les 3 endpoints post-materialize quand l'intake_event n'a pas
   * de `resolved_project_id` (état incohérent post-materialize — ne devrait
   * jamais arriver mais defensive guard). 409.
   */
  INTAKE_EVENT_ACTION_MISSING_PROJECT: {
    kind: "error",
    messages: {
      en: "This intake event is not linked to a project. Materialize it first.",
      fr: "Cet événement n'est pas lié à un projet. Matérialisez-le d'abord.",
    },
  },

  /**
   * Émis par `POST /:id/create-submission-from-file` quand une submission
   * avec le même `insurer_code` existe déjà sur le project cible (unique
   * DB constraint `(project_id, insurer_code)` — cf. 0003). 409.
   * `details.insurer_code` porte la valeur en conflit (info non-sensible,
   * broker connaît déjà les insurers du projet).
   */
  INTAKE_EVENT_ACTION_DUPLICATE_SUBMISSION: {
    kind: "error",
    messages: {
      en: "A submission from this insurer already exists on the project.",
      fr: "Une soumission de cet assureur existe déjà sur le projet.",
    },
  },

  /**
   * Émis par les 3 endpoints post-materialize (finding F18) quand
   * l'intake_event a déjà une `resolved_quote_request_id` (la matérialisation
   * a créé une QR en laissant `doc_direction='unknown'`). L'event est résolu
   * en quote request → plus aucune action post-materialize possible
   * (anti-double-QR / submission incompatible / ré-usage du document source).
   * Code distinct de `ALREADY_DIRECTED` : ce dernier serait trompeur ici, la
   * direction restant littéralement `'unknown'`. 409.
   */
  INTAKE_EVENT_ACTION_ALREADY_QUOTE_REQUESTED: {
    kind: "error",
    messages: {
      en: "This intake event already has a quote request and cannot take another post-materialize action.",
      fr: "Cet événement a déjà une quote request ; aucune autre action post-matérialisation n'est possible.",
    },
  },

  // --- MISSING_INFORMATION (1 code) ------------------------------------------

  /**
   * Émis par GET `/v1/missing-information/:id` quand l'entrée est
   * introuvable. 404. `details.resource_id` porte l'UUID demandé.
   * PO-Q3 9.K.9 : préfixe `MISSING_INFORMATION_*` complet (pas
   * `MISSING_INFO_*`) — parité avec nom de route et table DB
   * `app.missing_information`.
   */
  MISSING_INFORMATION_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Missing information entry not found.",
      fr: "Missing information introuvable.",
    },
  },

  /**
   * Émis par PATCH `/v1/missing-information/:id/fulfill` (9.I.1) quand le
   * `status` actuel n'autorise pas la transition `fulfill`. 409.
   *
   * Transitions autorisées Phase 1 :
   *   `open | in_progress → fulfilled`
   *
   * Tout autre status courant (`fulfilled`, `waived`, `superseded`) =>
   * INVALID_TRANSITION. Couvre aussi les race conditions concurrency
   * (rowCount=0 sur l'UPDATE atomic — WHERE status IN ('open','in_progress')
   * — parité 9.G.1 INTAKE_EVENTS_INVALID_TRANSITION).
   *
   * Pas de `details` projeté wire-side (anti-leak status interne ; broker
   * peut re-fetch GET /:id pour voir le status courant).
   */
  MISSING_INFORMATION_INVALID_TRANSITION: {
    kind: "error",
    messages: {
      en: "Missing information entry cannot transition from its current status.",
      fr: "L'entrée d'information manquante ne peut pas changer depuis son statut actuel.",
    },
  },

  // --- DOCUMENT_LINKS (2 codes) ----------------------------------------------

  /**
   * Émis par GET `/v1/document-links` quand zéro filtre fourni
   * (`file_id`, `attachable_kind`, `attachable_id` tous absents). 400.
   * Pattern F-01 (anti-scan complet — parité conceptuelle avec
   * `LOOKUP_*_FILTER_REQUIRED` 9.K.1/9.K.2 et
   * `RETENTION_AUDIT_LOG_FILTER_REQUIRED` 9.K.8).
   *
   * PO-Q2 9.K.9 : `details.allowed_filters` porte la liste exhaustive
   * des filtres acceptés (machine-data agent IA + UX).
   */
  DOCUMENT_LINKS_FILTER_REQUIRED: {
    kind: "error",
    messages: {
      en: "At least one filter is required: file_id, attachable_kind, or attachable_id.",
      fr: "Au moins un filtre requis : file_id, attachable_kind ou attachable_id.",
    },
  },

  /**
   * Émis par GET `/v1/document-links/:id` quand le lien est introuvable.
   * 404. `details.resource_id` porte l'UUID demandé.
   *
   * Note : `app.document_links` n'a PAS de soft-delete (`deleted_at`
   * absent — cf. 9.E.1) — le 404 signale donc une absence physique,
   * pas une suppression logique.
   */
  DOCUMENT_LINKS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Document link not found.",
      fr: "Document link introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/document-links` (Chunk 9.J) quand le `file_id`
   * fourni est introuvable OU appartient à un autre tenant. 404.
   *
   * **Anti-leak unique** (parité 9.G.1 `INTAKE_EVENTS_RESOLVE_TARGET_NOT_FOUND`)
   * : un seul code couvre les 2 cas not_found vs cross-tenant. Évite de
   * signaler à un broker l'existence d'un fichier d'un autre tenant.
   *
   * `details.resource_id` porte l'UUID `file_id` demandé.
   */
  DOCUMENT_LINKS_FILE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "File not found.",
      fr: "Fichier introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/document-links` (Chunk 9.J) quand le couple
   * `(attachable_kind, attachable_id)` pointe vers une row inexistante
   * OU appartenant à un autre tenant. 404.
   *
   * **Anti-leak unique** (parité 9.G.1 + DOCUMENT_LINKS_FILE_NOT_FOUND
   * 9.J ci-dessus). `details.resource_id` porte l'UUID `attachable_id`
   * demandé ; `details.attachable_kind` porte le kind pour disambiguation.
   *
   * Distinct de `DOCUMENT_LINKS_KIND_RESERVED` (422) — ici le kind est
   * actif (un des 5 : company/project/quote_request/submission/intake_event)
   * mais la row cible n'existe pas dans la table correspondante.
   */
  DOCUMENT_LINKS_ATTACHABLE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Target entity not found.",
      fr: "Entité cible introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/document-links` (Chunk 9.J) quand l'`attachable_kind`
   * fourni fait partie des 4 kinds **réservés** déclarés dans l'enum
   * `app.attachable_kind` mais sans table cible créée Phase 1 : `task`,
   * `report`, `offer`, `employee_census`. 422.
   *
   * Le trigger DB `validate_attachable_exists()` (0021:478) raise une
   * exception PG sur ces kinds — le handler Phase 1 9.J fait un pré-check
   * Zod-side (enum strict 5 kinds actifs) qui rejette plus tôt avec un
   * code dédié plutôt que de catcher la PG exception générique.
   *
   * `details.attachable_kind` porte le kind rejeté pour clarté UX/agent IA.
   */
  DOCUMENT_LINKS_KIND_RESERVED: {
    kind: "error",
    messages: {
      en: "Attachable kind is reserved for future use.",
      fr: "Type d'attachement réservé pour un usage futur.",
    },
  },

  // --- AGENT_IDENTITIES (3 codes — Chunk 9.M.5 D30) --------------------------

  /**
   * Émis par GET `/v1/agent-identities/:id` ou PATCH du même endpoint quand
   * l'agent_identity demandée est introuvable (id inexistant) OU appartient
   * à un tenant que l'appelant ne peut pas voir (anti-leak cross-tenant).
   * 404.
   *
   * **Anti-leak unique** (parité 9.J `DOCUMENT_LINKS_NOT_FOUND` /
   * `DOCUMENT_LINKS_FILE_NOT_FOUND`) : un seul code couvre les 2 cas
   * not_found vs cross-tenant invisible.
   *
   * `details.resource_id` porte l'UUID `agent_identity_id` demandé.
   */
  AGENT_IDENTITIES_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Agent identity not found.",
      fr: "Identité d'agent introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/agent-identities` (Chunk 9.M.5 D30) quand le
   * `kind` fourni est hors enum DB `('ai_operator', 'external_system')`.
   * 422.
   *
   * Le CHECK DB `agent_identities_kind_chk` rebondit sinon en exception PG
   * générique non-déterministe — le handler Phase 1 9.M.5 fait un pré-check
   * Zod-side (enum strict) qui rejette plus tôt avec un code dédié plutôt
   * que de catcher la PG exception générique.
   *
   * `details.kind` porte la valeur rejetée pour clarté UX/agent IA.
   */
  AGENT_IDENTITIES_KIND_INVALID: {
    kind: "error",
    messages: {
      en: "Agent identity kind is invalid.",
      fr: "Type d'identité d'agent invalide.",
    },
  },

  /**
   * Émis par POST `/v1/agent-identities` (Chunk 9.M.5 D30) quand le `tenant_id`
   * fourni n'existe pas (ou est soft-deleted). 404.
   *
   * Distinct du fallback générique `INTERNAL_ERROR` pour permettre au
   * super_admin de comprendre que le tenant cible est introuvable sans
   * exposer une stack PG.
   *
   * `details.resource_id` porte l'UUID `tenant_id` demandé.
   */
  AGENT_IDENTITIES_TENANT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Target tenant not found.",
      fr: "Tenant cible introuvable.",
    },
  },

  // --- QUOTE_REQUESTS (1 code) -----------------------------------------------

  /**
   * Émis par GET `/v1/quote-requests/:id` quand la quote_request est
   * introuvable (id inexistant ou soft-deleted). 404.
   * `details.resource_id` porte l'UUID demandé.
   */
  QUOTE_REQUESTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Quote request not found.",
      fr: "Quote request introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/send` (Chunk 9.L.1) quand le
   * status courant n'autorise pas la transition `→ sent`. 409.
   *
   * Transitions autorisées Phase 1 :
   *   `draft → sent`                 (premier envoi)
   *   `sent → sent`                  (idempotency retry pending recipients)
   *   `partially_received → sent`    (re-send pending recipients après quelques réponses)
   *   `fully_received → sent`        (théorique — déjà tout reçu, mais ne bloque pas Phase 1)
   *
   * Status terminal `cancelled` ou `expired` → INVALID_TRANSITION (action
   * commerciale annulée ou délai dépassé, pas de re-send possible Phase 1).
   *
   * Pas de `details` projeté wire-side (anti-leak status interne, broker
   * peut re-fetch GET /:id pour voir le status courant).
   */
  QUOTE_REQUESTS_INVALID_TRANSITION: {
    kind: "error",
    messages: {
      en: "Quote request cannot transition to 'sent' from its current status.",
      fr: "La quote request ne peut pas passer au statut 'sent' depuis son statut actuel.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/send` (Chunk 9.L.1) quand zéro
   * recipient n'existe pour cette quote_request (impossible d'envoyer un
   * mail à personne). 422.
   *
   * Phase 1 : recipients préexistants requis (création hors scope —
   * SQL admin fixtures pour smoke, chunk dédié futur pour route CRUD).
   * `details.quote_request_id` porte l'UUID pour clarté UX.
   */
  /**
   * Émis par POST `/v1/quote-requests/:id/send` (9.BC.1) quand au moins UN
   * destinataire en attente n'a aucune adresse.
   *
   * ## Pourquoi c'est un refus, et pas un envoi partiel
   *
   * L'envoi sautait silencieusement les destinataires sans adresse
   * (`if (!rec.recipient_email) continue`). Beneva partait, Sun Life était
   * ignorée, la demande passait `sent`, et le suivi affichait « en attente »
   * pour un assureur qui n'avait jamais rien reçu. Le conseiller n'avait aucun
   * moyen de s'en apercevoir.
   *
   * `details.insurers` NOMME les assureurs concernés — ce sont des codes de
   * catalogue, pas des données de client, et sans eux l'utilisateur devrait
   * deviner lequel corriger.
   */
  QUOTE_REQUEST_SEND_EMAIL_BLOCKED: {
    kind: "error",
    messages: {
      fr: "Envoi bloqué : une adresse assureur a signalé un courriel indésirable ou s’est désabonnée. Les sollicitations à cette adresse sont bloquées pour tout le cabinet.",
      en: "Sending blocked: an insurer address reported spam or unsubscribed. Insurer solicitations to this address are blocked throughout the brokerage.",
    },
  },
  QUOTE_REQUEST_SEND_RECIPIENT_WITHOUT_EMAIL: {
    kind: "error",
    messages: {
      en: "Some insurers have no email address. Add one for each before sending — nothing was sent.",
      fr: "Des assureurs n'ont aucune adresse courriel. Ajoutez-en une pour chacun avant d'envoyer — rien n'a été envoyé.",
    },
  },

  QUOTE_REQUESTS_NO_RECIPIENTS: {
    kind: "error",
    messages: {
      en: "Quote request has no recipients to send to.",
      fr: "Aucun assureur n’a été ajouté à cette demande. Ajoutez-en un avant d’envoyer.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/send` (Chunk 9.L.1) quand TOUS
   * les recipients pending échouent au moment du send (Mailgun HTTP 4xx/5xx
   * ou network error). 500.
   *
   * Si ≥1 recipient OK → réponse 200 + `recipients_summary.sent ≥ 1` même
   * si certains fail (partial success). Ce code 500 est strict « zéro
   * succès, tout fail ».
   *
   * Recipients restent `status='pending'` côté DB (forensic preserve qui
   * a échoué via logs pino structured). QR.status reste `draft` ou
   * `sent` selon état pré-call (pas de mutation status si zero succès).
   * Re-call POST /send après remediation Mailgun → idempotent retry.
   *
   * `details.failed_count` porte le nombre de recipients en échec pour
   * clarté UX broker.
   */
  QUOTE_REQUESTS_EMAIL_SEND_FAILED: {
    kind: "error",
    messages: {
      en: "All recipient emails failed to send. Please check email service status and retry.",
      fr: "Tous les emails aux destinataires ont échoué. Vérifier l'état du service email et relancer.",
    },
  },

  // ===========================================================================
  // Chunk 9.L.7 — Quote requests + recipients CRUD broker write actions.
  // 6 nouveaux codes bilingues pour les transitions create/update/cancel + add/
  // remove recipient. Pas de nouveau code GATING_HUMAN_REQUIRED (réutilisé
  // 9.H.5 pour `quote_request.cancel`). Migration 0037 seed la default policy
  // `quote_request.cancel agent_allowed=false`.
  // ===========================================================================

  /**
   * 9.L.7 — CANCEL transition invalide.
   * status courant ∉ {draft, sent, partially_received} (fully_received/expired/
   * cancelled rejetés — terminal). Distinct de `QUOTE_REQUESTS_INVALID_TRANSITION`
   * (réservé send 9.L.1) pour clarté UX broker + futurs analytics.
   */
  QUOTE_REQUESTS_CANCEL_INVALID_TRANSITION: {
    kind: "error",
    messages: {
      en: "Quote request cannot be cancelled from its current status.",
      fr: "La quote request ne peut pas être annulée depuis son statut actuel.",
    },
  },

  /**
   * 9.L.7 — Add recipient refusé : QR.status ∉ {draft, sent}.
   * (cancelled/expired/partially_received/fully_received interdits — pas
   * d'ajout d'assureur après transition terminale ou réponse partielle).
   */
  QUOTE_REQUESTS_RECIPIENTS_QR_LOCKED: {
    kind: "error",
    messages: {
      en: "Cannot add or remove recipients while the quote request is in this status.",
      fr: "Impossible d'ajouter ou de retirer un destinataire dans ce statut de demande.",
    },
  },

  /**
   * 9.L.7 — Add recipient duplicate.
   * Unique constraint DB `(quote_request_id, insurer_code)` 0021:260.
   */
  QUOTE_REQUEST_RECIPIENTS_DUPLICATE: {
    kind: "error",
    messages: {
      en: "A recipient with this insurer_code already exists on this quote request.",
      fr: "Un destinataire avec ce code d'assureur existe déjà sur cette demande.",
    },
  },

  /**
   * 9.L.7 — Recipient introuvable (UUID inexistant, cross-QR, cross-tenant).
   * Anti-leak unique (parité 9.J FILE/ATTACHABLE NOT_FOUND).
   */
  QUOTE_REQUEST_RECIPIENTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Recipient not found on this quote request.",
      fr: "Destinataire introuvable sur cette demande.",
    },
  },

  /**
   * 9.L.7 — Remove recipient refusé : recipient.status ≠ 'pending'.
   * Sent/responded/declined/expired → conservés pour forensic audit
   * (changes.before via trigger `audit_quote_request_recipients` 0021:557).
   */
  QUOTE_REQUEST_RECIPIENTS_NOT_REMOVABLE: {
    kind: "error",
    messages: {
      en: "Recipient cannot be removed once it has been sent or responded.",
      fr: "Le destinataire ne peut plus être retiré après envoi ou réponse.",
    },
  },

  /**
   * 9.T.5.v3.7 — PATCH recipient refusé : recipient.status ≠ 'pending'.
   * Un recipient déjà `sent/responded/declined/expired` est locked pour
   * préserver l'intégrité audit (`audit_quote_request_recipients` 0021:557)
   * et éviter les incohérences (adresse email changée après envoi effectif).
   */
  QUOTE_REQUEST_RECIPIENTS_NOT_EDITABLE: {
    kind: "error",
    messages: {
      en: "Recipient cannot be updated once it has been sent or responded.",
      fr: "Le destinataire ne peut plus être modifié après envoi ou réponse.",
    },
  },

  /**
   * 9.L.7 — Create QR avec project_id introuvable ou cross-tenant.
   * Anti-leak unique parité 9.G.1 RESOLVE_TARGET_NOT_FOUND / 9.J
   * ATTACHABLE_NOT_FOUND : un broker ne peut pas distinguer « projet
   * inexistant » de « projet dans un autre tenant » via la réponse API.
   */
  QUOTE_REQUESTS_PROJECT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Project not found or out of tenant scope.",
      fr: "Projet introuvable ou hors scope tenant.",
    },
  },

  // ===========================================================================
  // Chunk 9.K.10 — D58.1 batch 5 : routes CRUD bilingual
  //   - `apps/api/src/routes/tenants.ts`        (5 codes uniques, 7 sites)
  //   - `apps/api/src/routes/users.ts`          (10 codes uniques, 17 sites)
  //   - `apps/api/src/routes/auth.ts`           (3 codes uniques, 3 sites)
  //   - `apps/api/src/routes/integrations.ts`   (3 codes uniques, 3 sites)
  // → 30 sites `ApiErrors.* (msg FR strict)` migrés bilingue ADR 0027 P-3
  // via `bilingualHttpError(code, status, details?)`. 21 codes uniques
  // ajoutés (mutualisation `USERS_NOT_FOUND` ×4 + `USERS_ADMIN_ONLY` ×4
  // sur verbes différents — PO-Q4 9.K.10).
  // + 1 résidu volontaire `ApiErrors.validation("Payload invalide", parsed.error.flatten())`
  // dans `tenants.ts` L213 conservé sous PO-Q3 (pattern handler global Zod 9.K.5 / D58.6).
  //
  // Convention 9.K.10 : préfixe par entité pluriel (PO-Q3 9.K.1 + PO-Q4 9.K.9) —
  // `TENANTS_*`, `USERS_*`. `AUTH_*` et `INTEGRATIONS_*` portent le préfixe
  // singulier de la surface (parité conceptuelle `LOOKUP_*` 9.K.1/9.K.2 et
  // `RETENTION_*` 9.K.8 — la surface est conceptuellement singulière).
  //
  // Décisions PO 9.K.10 :
  //   - PO-Q2 : `AUTH_INVALID_CREDENTIALS` wording neutre EN/FR (P-9 ADR 0027) —
  //     ne distingue jamais "user not found" vs "wrong password" vs "email
  //     invalide". Wording FR conservé `"Email ou mot de passe invalide"` ;
  //     EN strictement parallèle.
  //   - PO-Q3 : tenants.ts L206 (`safeParse` PATCH) → laissé en `ApiErrors.
  //     validation()` legacy. Pas de migration vers code dédié, pour respecter
  //     pattern 9.K.5 où `VALIDATION_ERROR` est servi par le handler global
  //     pour les erreurs Zod. Voir note dans le diff de tenants.ts.
  //   - PO-Q4 : `USERS_NOT_FOUND` mutualisé sur GET/PATCH/PATCH-role/DELETE
  //     (sémantique métier identique = "user du tenant introuvable, soft-deleted
  //     ou inexistant"). `USERS_ADMIN_ONLY` mutualisé sur les contextes admin
  //     similaires (POST invite + PATCH role + DELETE). Différenciation
  //     uniquement quand la sémantique métier est réellement distincte
  //     (ex. `USERS_PATCH_ADMIN_OR_SELF_ONLY` ≠ `USERS_ADMIN_ONLY` car
  //     "admin OU self" est un autre cas que "admin seul").
  //   - PO-Q6 : `query_audit_log` / `Routes 9.F (audit_log)` mentionnés dans
  //     `docs/design/i18n-audit-9k0.md` sont **prospectifs** (pas de route
  //     `apps/api/src/routes/audit-log.ts` actuellement) — `audit-log.ts` retiré
  //     du scope batch 5.
  // ===========================================================================

  // --- TENANTS (5 codes) -----------------------------------------------------

  /**
   * Émis par GET/PATCH/DELETE `/v1/tenants/:id` quand le tenant est
   * introuvable (id inexistant ou soft-deleted). 404.
   * `details.resource_id` porte l'UUID demandé.
   */
  TENANTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Tenant not found.",
      fr: "Tenant introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/tenants` quand le slug existe déjà (violation
   * UNIQUE Postgres `23505`). 409. `details.slug` porte la valeur en
   * conflit. Parité 9.K.6 `COMPANIES_DUPLICATE_KEY` côté sémantique
   * conflit applicatif (mais préfixe `TENANTS_*` — PO-Q4 9.K.10).
   */
  TENANTS_SLUG_TAKEN: {
    kind: "error",
    messages: {
      en: "Tenant slug already taken.",
      fr: "Slug de tenant déjà utilisé.",
    },
  },

  /**
   * Émis par PATCH `/v1/tenants/:id` quand l'auth est de type `api_key`
   * (cas dégénéré — la route demande explicitement un user humain pour
   * tracer l'acteur). 403. Pas de details (pas d'identifiant exploitable).
   */
  TENANTS_USER_AUTH_REQUIRED: {
    kind: "error",
    messages: {
      en: "User authentication required for this operation.",
      fr: "Authentification utilisateur requise pour cette opération.",
    },
  },

  /**
   * Émis par PATCH `/v1/tenants/:id` quand un tenant_admin tente de
   * modifier un tenant qui n'est pas le sien (anti-IDOR applicatif —
   * belt & braces vs RLS). 403. `details.resource_id` porte l'UUID
   * du tenant cible (pas de fuite — l'acteur connaît déjà l'id qu'il
   * a tenté d'accéder).
   */
  TENANTS_FORBIDDEN_NOT_OWN: {
    kind: "error",
    messages: {
      en: "Cannot modify a tenant other than your own.",
      fr: "Impossible de modifier un tenant autre que le vôtre.",
    },
  },

  /**
   * Émis par PATCH `/v1/tenants/:id` quand le payload nettoyé (post
   * `undefined` filter) est vide. 400. Parité 9.K.6
   * `COMPANIES_NO_FIELDS_TO_UPDATE` / 9.K.7 `BROKERS_NO_FIELDS_TO_UPDATE` /
   * 9.K.9 `SUBMISSIONS_NO_FIELDS_TO_UPDATE` — code dédié par entité.
   */
  TENANTS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },

  // --- USERS (10 codes) ------------------------------------------------------

  /**
   * Émis par GET/PATCH/PATCH-role/DELETE `/v1/users/:id` quand le user est
   * introuvable (id inexistant ou soft-deleted). 404.
   * `details.resource_id` porte l'UUID demandé. PO-Q4 9.K.10 : code mutualisé
   * sur les 4 verbes (sémantique métier identique).
   */
  USERS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "User not found.",
      fr: "Utilisateur introuvable.",
    },
  },

  /**
   * Émis par POST invite / PATCH /:id/role / DELETE quand l'acteur n'est
   * pas admin (ni super_admin, ni tenant_admin, ni api_key). 403. Pas de
   * details. PO-Q4 9.K.10 : code mutualisé sur les contextes "admin seul".
   */
  USERS_ADMIN_ONLY: {
    kind: "error",
    messages: {
      en: "Admin role required.",
      fr: "Réservé aux administrateurs.",
    },
  },

  /**
   * Émis par `assertCanAssignRole` quand un acteur tenant_admin (ou api_key)
   * tente de créer/promouvoir un user au rôle `tenant_admin` ou
   * `super_admin`. 403. Pas de details (anti-énum). Sémantique distincte
   * de `USERS_ADMIN_ONLY` : ici l'acteur EST admin, mais ne peut pas
   * escalader.
   */
  USERS_ROLE_ESCALATION_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Only super_admin can create or promote an admin.",
      fr: "Seul un super_admin peut créer ou promouvoir un administrateur.",
    },
  },

  /**
   * Émis par POST `/v1/users/invite` quand l'acteur super_admin invite
   * un user non-super_admin sans fournir `tenant_id` (body) ni
   * `x-tenant-id` (header). 400. Pas de details (le contexte est connu
   * de l'acteur).
   */
  USERS_TENANT_REQUIRED_FOR_INVITE: {
    kind: "error",
    messages: {
      en: "Tenant id required to invite a non-super_admin user — provide tenant_id (body) or x-tenant-id (header).",
      fr: "tenant_id requis pour inviter un user non-super_admin — fournir tenant_id (body) ou x-tenant-id (header).",
    },
  },

  /**
   * Émis par POST `/v1/users/invite` quand un acteur non-super_admin tente
   * de créer un super_admin (ceinture-bretelles après `assertCanAssignRole`).
   * 403. Sémantique distincte de `USERS_ROLE_ESCALATION_FORBIDDEN` car ce
   * code est servi sur le chemin spécifique POST invite avec rôle cible
   * `super_admin` non-tenant.
   */
  USERS_SUPER_ADMIN_CREATION_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Cannot create a super_admin.",
      fr: "Impossible de créer un super_admin.",
    },
  },

  /**
   * Émis par POST `/v1/users/invite` quand l'appel à Supabase Auth
   * `inviteUser()` échoue. 400. `details.upstream_error` porte le message
   * d'erreur Supabase (pas de fuite — message déjà tronqué côté Supabase).
   */
  USERS_INVITE_FAILED: {
    kind: "error",
    messages: {
      en: "Invitation failed.",
      fr: "Invitation échouée.",
    },
  },

  /**
   * Émis par PATCH `/v1/users/:id` quand l'acteur n'est ni admin ni le
   * propriétaire du compte cible. 403. Sémantique distincte de
   * `USERS_ADMIN_ONLY` : ici "admin OU self" est autorisé, donc le rejet
   * a une cause différente.
   */
  /**
   * Émis par POST `/v1/users/:id/resend-invite` quand le membre visé n'est pas
   * en attente d'invitation. 409.
   *
   * Renvoyer une invitation à un compte déjà actif n'a pas de sens — la personne
   * a un mot de passe, et le lien d'invitation lui ouvrirait une session sans le
   * demander. `details.status` est **volontairement absent** : l'appelant est
   * déjà admin du cabinet et voit le statut à l'écran ; l'exposer dans l'erreur
   * n'apporterait rien et élargirait la surface pour rien.
   */
  /**
   * Émis par POST `/v1/quote-requests/:id/spec` quand la réponse de formulaire
   * visée est introuvable, appartient à un autre cabinet, ou ne porte pas sur le
   * même dossier que la demande. 404.
   *
   * **Un seul code pour ces trois cas, volontairement** : distinguer « n'existe
   * pas » de « existe ailleurs » dirait à l'appelant qu'une réponse existe dans
   * un autre cabinet.
   */
  /**
   * Émis par POST `/v1/quote-requests/:id/send` quand le cahier de charges a
   * changé depuis que son artefact a été rendu. 409.
   *
   * `details.spec_id` permet de régénérer sans deviner. L'ancien artefact n'est
   * **pas** écrasé : il reste la preuve de ce qui avait été préparé.
   */
  QUOTE_REQUEST_SEND_SPEC_STALE: {
    kind: "error",
    messages: {
      en: "The specification changed after its document was produced; regenerate it before sending.",
      fr: "Le cahier de charges a changé depuis la production de son document ; régénérez-le avant d'envoyer.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/send` quand les pièces jointes
   * dépassent ce qu'un courriel peut porter. 422.
   *
   * Vérifié à la MISE EN FILE, jamais à l'envoi : échouer à l'expédition ferait
   * découvrir le problème des heures plus tard, dans une file que personne ne
   * regarde.
   */
  QUOTE_REQUEST_SEND_ATTACHMENTS_TOO_LARGE: {
    kind: "error",
    messages: {
      en: "The attachments exceed what an email can carry.",
      fr: "Les pièces jointes dépassent ce qu'un courriel peut transporter.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/spec/:specId/regenerate` quand le
   * cahier de charges visé n'appartient pas à cette demande. 404.
   */
  QUOTE_REQUEST_SPEC_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Specification not found for this quote request.",
      fr: "Cahier de charges introuvable pour cette demande de soumission.",
    },
  },

  QUOTE_REQUEST_SPEC_RESPONSE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Form response not found for this quote request.",
      fr: "Réponse de formulaire introuvable pour cette demande de soumission.",
    },
  },

  /**
   * Émis quand la réponse est encore un brouillon. 409.
   *
   * Un brouillon change encore : en faire un cahier de charges produirait un
   * document qui ne correspond à rien de stable.
   */
  QUOTE_REQUEST_SPEC_RESPONSE_NOT_SUBMITTED: {
    kind: "error",
    messages: {
      en: "This form response is still a draft; submit it before attaching it.",
      fr: "Cette réponse est encore un brouillon ; soumettez-la avant de la rattacher.",
    },
  },

  /**
   * Émis quand la demande porte déjà ce cahier de charges, ou déjà un cahier de
   * charges à cette position. 409.
   */
  QUOTE_REQUEST_SPEC_ALREADY_ATTACHED: {
    kind: "error",
    messages: {
      en: "This quote request already carries this specification.",
      fr: "Cette demande porte déjà ce cahier de charges.",
    },
  },

  /**
   * Émis quand on tente de rattacher un cahier de charges à une demande qui
   * n'est plus modifiable — déjà envoyée, annulée ou expirée. 409.
   *
   * Changer ce qui est demandé après l'envoi ferait diverger le document reçu par
   * l'assureur et l'état du dossier.
   */
  QUOTE_REQUEST_SPEC_REQUEST_NOT_EDITABLE: {
    kind: "error",
    messages: {
      en: "This quote request can no longer be modified.",
      fr: "Cette demande de soumission n'est plus modifiable.",
    },
  },

  USERS_RESEND_INVITE_NOT_PENDING: {
    kind: "error",
    messages: {
      en: "This member is not awaiting an invitation.",
      fr: "Ce membre n'est pas en attente d'invitation.",
    },
  },

  /**
   * Émis par POST `/v1/users/:id/resend-invite` quand le membre n'appartient à
   * aucun cabinet. 409.
   *
   * `email_outbox.tenant_id` est `NOT NULL` : un `super_admin`, qui n'a par
   * construction aucun cabinet (`chk_super_admin_no_tenant`), ne peut pas être
   * réinvité par ce chemin. On le DIT plutôt que de laisser croire à un envoi.
   */
  USERS_RESEND_INVITE_NO_TENANT: {
    kind: "error",
    messages: {
      en: "This member belongs to no firm; the invitation cannot be sent.",
      fr: "Ce membre n'appartient à aucun cabinet ; l'invitation ne peut pas être envoyée.",
    },
  },

  AUTH_CABINET_ACCESS_UNAVAILABLE: {
    kind: "error",
    messages: {
      en: "This firm access is unavailable. Select an active firm or accept your pending access.",
      fr: "Cet accès au cabinet est indisponible. Sélectionnez un cabinet actif ou acceptez votre accès en attente.",
    },
  },
  MEMBERSHIP_ACCEPTANCE_REQUIRED: {
    kind: "error",
    messages: {
      en: "The account owner must accept this firm access before it can be activated.",
      fr: "Le titulaire du compte doit accepter cet accès au cabinet avant son activation.",
    },
  },
  USERS_PATCH_ADMIN_OR_SELF_ONLY: {
    kind: "error",
    messages: {
      en: "Only the account owner or a platform administrator can update this profile.",
      fr: "Seuls le titulaire du compte ou un administrateur de la plateforme peuvent modifier ce profil.",
    },
  },

  /**
   * Émis par PATCH `/v1/users/:id` ou PATCH `/v1/users/:id/role` quand
   * le payload nettoyé est vide. 400. Parité avec
   * `TENANTS_NO_FIELDS_TO_UPDATE` / `SUBMISSIONS_NO_FIELDS_TO_UPDATE`.
   * Mutualisé entre les 2 verbes PATCH (sémantique identique côté user).
   */
  USERS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },

  /**
   * Émis par PATCH `/v1/users/:id/role` quand l'acteur tente de modifier
   * son propre rôle. 400. Anti-auto-démotion. Pas de details.
   */
  USERS_SELF_ROLE_CHANGE_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Cannot change your own role — ask another admin.",
      fr: "Impossible de modifier son propre rôle — demande à un autre admin.",
    },
  },

  /**
   * Émis par DELETE `/v1/users/:id` quand l'acteur tente de supprimer son
   * propre compte. 400. Anti-auto-destruction.
   */
  USERS_SELF_DELETE_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Cannot delete your own account.",
      fr: "Impossible de supprimer son propre compte.",
    },
  },

  // --- AUTH (3 codes) --------------------------------------------------------

  /**
   * Émis par POST `/v1/auth/login` quand `signInWithPassword` échoue
   * (mauvais mot de passe, email inconnu, compte banni, etc.). 401.
   *
   * **Sécurité P-9 ADR 0027 (PO-Q2 9.K.10)** : wording strictement neutre
   * EN/FR — ne distingue **jamais** "user not found" vs "wrong password"
   * vs "email invalide" vs "compte désactivé". Le détail Supabase est
   * loggué côté serveur (`logger.info`) mais **jamais** projeté au client.
   * Pas de details (anti-énum).
   */
  AUTH_INVALID_CREDENTIALS: {
    kind: "error",
    messages: {
      en: "Invalid email or password.",
      fr: "Email ou mot de passe invalide.",
    },
  },

  /**
   * Émis par GET `/v1/auth/me` quand le contexte d'auth est absent ou
   * n'est pas de type `user` (ceinture-bretelles après `requireUser()`).
   * 401. Pas de details.
   */
  AUTH_USER_REQUIRED: {
    kind: "error",
    messages: {
      en: "User authentication required.",
      fr: "Authentification utilisateur requise.",
    },
  },

  /**
   * Émis par GET `/v1/auth/me` quand le JWT est valide mais le profil
   * `app.users` correspondant est absent (compte supprimé en arrière-plan,
   * désynchronisation Auth ↔ DB). 404. Pas de details (l'acteur connaît
   * déjà son propre id).
   */
  AUTH_PROFILE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "User profile not found.",
      fr: "Profil utilisateur introuvable.",
    },
  },

  // --- INTEGRATIONS (3 codes) ------------------------------------------------

  /**
   * Émis par GET `/v1/integrations/playbooks/:slug` quand le slug n'est
   * pas dans la whitelist `PLAYBOOKS`. 404. `details.slug` porte la valeur
   * demandée (pas de fuite — c'est l'input de l'appelant).
   */
  INTEGRATIONS_PLAYBOOK_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Playbook not found.",
      fr: "Playbook introuvable.",
    },
  },

  /**
   * Émis par GET `/v1/integrations/playbooks/:slug` quand le chemin
   * résolu sort du dossier `PLAYBOOKS_DIR` (path traversal — quasi-mort
   * en pratique grâce à la whitelist, mais belt & braces). 403. Pas de
   * details (anti-recon).
   */
  INTEGRATIONS_PLAYBOOK_PATH_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Playbook path out of scope.",
      fr: "Chemin de playbook hors périmètre.",
    },
  },

  /**
   * Émis par GET `/v1/integrations/playbooks/:slug` quand le slug est
   * whitelisté mais le fichier .md est absent du disque (erreur de
   * déploiement). 500. `details.slug` + `details.upstream_error` portent
   * la cause exacte pour les ops.
   */
  INTEGRATIONS_PLAYBOOK_FILE_MISSING: {
    kind: "error",
    messages: {
      en: "Playbook declared but missing on disk.",
      fr: "Playbook déclaré mais introuvable sur disque.",
    },
  },

  // ===========================================================================
  // Chunk 9.K.11 — D58.1 batch 6 : routes legacy `files` + `extractions`
  // + `brokers` résiduel — clôture migration `ApiErrors.*` runtime.
  //   - `apps/api/src/routes/files.ts`        (4 codes uniques, 10 sites)
  //   - `apps/api/src/routes/extractions.ts`  (8 codes uniques, 9 sites)
  //   - `apps/api/src/routes/brokers.ts`      (1 code unique,  4 sites
  //                                            résiduels `ApiErrors.unauthorized()`
  //                                            non migrés en 9.K.7 montage auth)
  // → 23 sites `ApiErrors.* (msg FR strict)` migrés bilingue ADR 0027 P-3
  // via `bilingualHttpError(code, status, details?)`. 13 codes uniques
  // ajoutés.
  //
  // Décisions PO 9.K.11 (rappel — confirmées avant patch) :
  //   - PO-Q1 : `brokers.ts` `ApiErrors.unauthorized()` (4 sites montage 401)
  //     → migration `BROKERS_USER_AUTH_REQUIRED` (parité 9.K.10
  //     `TENANTS_USER_AUTH_REQUIRED`) — cohérence stricte 100% bilingue.
  //   - PO-Q2 : `FILES_NOT_FOUND` mutualisé sur 4 verbes (POST /complete,
  //     GET /:id, GET /:id/download, DELETE) avec `details.context ∈
  //     {'complete','get','download','delete'}` — sémantique métier identique.
  //   - PO-Q3 : `FILES_FORBIDDEN_TENANT` un seul code + `details.scope_kind ∈
  //     {'project','submission','company'}` — anti-IDOR uniformisé.
  //   - PO-Q4 : `FILES_STATUS_CONFLICT` un seul code + `details.action ∈
  //     {'complete','download'}` + `details.current_status` — code unifié.
  //   - PO-Q5 : `FILES_UPLOAD_NOT_CONFIRMED` wording neutre, **sans**
  //     `details.bucket_*` (P-9 anti-recon storage interne).
  //   - PO-Q6 : `EXTRACTIONS_OVERRIDE_USER_ONLY` (P-9 neutre, ne distingue
  //     pas api_key vs agent — ne mentionne que la contrainte humaine).
  //   - PO-Q7 : `EXTRACTIONS_FIELD_NOT_FOUND` mutualisé SELECT pré-update +
  //     RETURNING post-update avec `details.context ∈ {'pre_update','post_update'}`.
  //   - PO-Q8 : alias `message` rétrocompat conservé (parité 9.K.10).
  //   - PO-Q9 : `EXTRACTIONS_FILE_NOT_READY` séparé de
  //     `FILES_STATUS_CONFLICT` car sémantique métier distincte (côté
  //     extraction, on attend explicitement `status=ready` avant de lancer
  //     un job — pas un conflit générique).
  //   - PO-Q10 : `EXTRACTIONS_SUBMISSION_*` et `EXTRACTIONS_FILE_*`
  //     séparés (4 codes distincts) car la résolution des deux entités
  //     (submission_id, file_id) se fait sur deux requêtes SQL distinctes
  //     dans la même route — l'agent appelant doit pouvoir distinguer
  //     « submission inexistante » de « file inexistant ».
  //
  // Hors scope 9.K.11 (D58.1-a fermée, D58.1 globale reste 🟡 ouverte) :
  //   - tenants.ts L213 : résidu volontaire `ApiErrors.validation` PO-Q3
  //     9.K.10 conservé (pattern handler global Zod 9.K.5 / D58.6).
  //   - retrait alias `message` rétrocompat : reporté chunk dédié final
  //     post-D58.3 frontend.
  //   - audit_log REST : pas de route `apps/api/src/routes/audit-log.ts`,
  //     donc non-applicable (table SQL backend triggers couverte par
  //     `retention_audit_log` lecture déjà migrée 9.K.8).
  // ===========================================================================

  // --- FILES (4 codes) -------------------------------------------------------

  /**
   * Émis par POST `/v1/files/uploads/initiate` quand le body ne fournit
   * AUCUN parent (ni `project_id`, ni `submission_id`, ni `company_id`).
   * 400. `details.field` vaut `"parent"`. Migration 9.K.12 (D58.1-b
   * refines schemas) du `.refine()` Zod intra-schema vers handler
   * bilingue ADR 0027 (P-3). Évite un fichier orphelin (la règle est
   * ré-appliquée côté DB par les CHECKs et les RLS).
   */
  FILES_UPLOAD_PARENT_REQUIRED: {
    kind: "error",
    messages: {
      en: "At least one parent is required: project_id, submission_id, or company_id.",
      fr: "Au moins un parent requis : project_id, submission_id ou company_id.",
    },
  },

  /**
   * Émis par POST `/v1/files/uploads/:id/complete`, GET `/v1/files/:id`,
   * GET `/v1/files/:id/download`, DELETE `/v1/files/:id` quand le fichier
   * est introuvable (id inexistant, soft-deleted, ou hors-tenant via RLS).
   * 404. `details.resource_id` porte l'UUID demandé. `details.context ∈
   * {'complete','get','download','delete'}` distingue le verbe d'origine.
   * PO-Q2 9.K.11 : code mutualisé sur 4 verbes (sémantique métier identique).
   */
  FILES_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "File not found.",
      fr: "Fichier introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/files/uploads/initiate` quand l'entité parente
   * (project / submission / company) est introuvable OU hors-tenant
   * (anti-IDOR applicatif — belt & braces vs RLS). 403. `details.scope_kind ∈
   * {'project','submission','company'}` indique l'entité parente bloquante.
   * `details.resource_id` porte l'UUID de l'entité parente refusée.
   * PO-Q3 9.K.11 : code mutualisé + scope_kind discriminant.
   */
  FILES_FORBIDDEN_TENANT: {
    kind: "error",
    messages: {
      en: "Parent resource not found or not in your tenant.",
      fr: "Ressource parente introuvable ou hors tenant.",
    },
  },

  /**
   * Émis par POST `/v1/files/uploads/:id/complete` (file pas en `pending`)
   * et GET `/v1/files/:id/download` (file pas en `ready`). 409.
   * `details.action ∈ {'complete','download'}` distingue le verbe.
   * `details.current_status` porte le status actuel (énum publique côté
   * client OK — parité `LEGAL_HOLDS_RELEASE_INVALID_STATUS` 9.K.8).
   * PO-Q4 9.K.11 : code unique + discriminant action.
   */
  FILES_STATUS_CONFLICT: {
    kind: "error",
    messages: {
      en: "File status does not allow this action.",
      fr: "Le statut du fichier ne permet pas cette action.",
    },
  },

  /**
   * Émis par POST `/v1/files/uploads/:id/complete` quand le HEAD bucket
   * échoue (l'objet n'existe pas — le client a fait `/initiate` mais
   * jamais le PUT vers la signed URL, ou le PUT a échoué). 400.
   * **Sécurité PO-Q5 9.K.11** : pas de `details.bucket_*` exposé
   * (anti-recon — le client n'a aucun besoin de connaître le nom du
   * bucket ou le path interne pour corriger son intégration).
   */
  FILES_UPLOAD_NOT_CONFIRMED: {
    kind: "error",
    messages: {
      en: "Upload not confirmed in storage — retry the upload then re-post /complete.",
      fr: "Upload non confirmé en bucket — relance l'upload puis re-poste /complete.",
    },
  },

  // --- EXTRACTIONS (8 codes) -------------------------------------------------

  /**
   * Émis par POST `/v1/extractions` quand la `submission_id` du body
   * est introuvable (id inexistant ou soft-deleted). 404.
   * `details.resource_id` porte l'UUID demandé. PO-Q10 9.K.11 : code
   * distinct de `EXTRACTIONS_FILE_NOT_FOUND` car les deux entités sont
   * résolues sur deux requêtes SQL séparées dans le même endpoint.
   */
  EXTRACTIONS_SUBMISSION_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Submission not found.",
      fr: "Soumission introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/extractions` quand la `submission_id` existe mais
   * n'appartient pas au tenant courant (anti-IDOR applicatif — belt &
   * braces vs RLS). 403. `details.resource_id` porte l'UUID demandé.
   */
  EXTRACTIONS_SUBMISSION_FORBIDDEN_TENANT: {
    kind: "error",
    messages: {
      en: "Submission is not in your tenant.",
      fr: "Soumission hors tenant.",
    },
  },

  /**
   * Émis par POST `/v1/extractions` quand la `file_id` du body est
   * introuvable (id inexistant ou soft-deleted). 404.
   * `details.resource_id` porte l'UUID demandé.
   */
  EXTRACTIONS_FILE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "File not found.",
      fr: "Fichier introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/extractions` quand la `file_id` existe mais
   * n'appartient pas au tenant courant (anti-IDOR applicatif). 403.
   * `details.resource_id` porte l'UUID demandé.
   */
  EXTRACTIONS_FILE_FORBIDDEN_TENANT: {
    kind: "error",
    messages: {
      en: "File is not in your tenant.",
      fr: "Fichier hors tenant.",
    },
  },

  /**
   * Émis par POST `/v1/extractions` quand la `file_id` est valide mais
   * pas en status `ready` (extraction nécessite que le binaire soit
   * confirmé en bucket via /complete). 409. `details.current_status`
   * porte le status actuel (énum publique côté client OK).
   * PO-Q9 9.K.11 : code distinct de `FILES_STATUS_CONFLICT` car
   * sémantique métier différente (contrainte d'extraction, pas un
   * conflit générique de cycle de vie).
   */
  EXTRACTIONS_FILE_NOT_READY: {
    kind: "error",
    messages: {
      en: "File status must be 'ready' before launching an extraction.",
      fr: "Le statut du fichier doit être « ready » avant de lancer une extraction.",
    },
  },

  /**
   * Émis par GET `/v1/extractions/:id` quand l'extraction est introuvable
   * (id inexistant — `app.extractions` n'a PAS de soft-delete, on garde
   * tout pour audit). 404. `details.resource_id` porte l'UUID demandé.
   */
  EXTRACTIONS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Extraction not found.",
      fr: "Extraction introuvable.",
    },
  },

  /**
   * Émis par PATCH `/v1/extracted-fields/:id` quand l'acteur n'est pas
   * un user humain (auth.kind !== 'user'). 403.
   *
   * **Sécurité P-9 ADR 0027 (PO-Q6 9.K.11)** : wording neutre — ne
   * distingue **jamais** « api_key » vs « agent » vs « auth absente ».
   * Le client appelant comprend uniquement « il faut un user humain ».
   * Pas de `details` (anti-énum). Sémantique métier : l'override
   * humain est explicitement réservé aux users car il alimente le gold
   * dataset few-shot (`app.corrections.corrected_by`).
   */
  EXTRACTIONS_OVERRIDE_USER_ONLY: {
    kind: "error",
    messages: {
      en: "Field override must be performed by a human user.",
      fr: "L'override d'un champ doit être effectué par un utilisateur humain.",
    },
  },

  /**
   * Émis par PATCH `/v1/extracted-fields/:id` quand le champ est
   * introuvable. 404. **Mutualisé sur deux endroits du même handler**
   * (PO-Q7 9.K.11) : `details.context ∈ {'pre_update','post_update'}`.
   *   - `pre_update` : SELECT initial pour récupérer l'ancienne valeur
   *     (archivage `app.corrections`) → champ inexistant ou hors-tenant.
   *   - `post_update` : RETURNING vide après UPDATE → race condition
   *     (le champ a été supprimé entre le SELECT et l'UPDATE).
   * `details.resource_id` porte l'UUID demandé.
   */
  EXTRACTIONS_FIELD_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Extracted field not found.",
      fr: "Champ extrait introuvable.",
    },
  },

  // --- BROKERS (1 code — résiduel D58.1-a non couvert en 9.K.7) -------------

  /**
   * Émis par POST `/v1/brokers/invite`, PATCH `/v1/brokers/:id`,
   * PATCH `/v1/brokers/:id/role`, DELETE `/v1/brokers/:id` quand le
   * contexte d'auth est absent (cas dégénéré — `requireAuth` middleware
   * a normalement déjà rejeté avant ; ceinture-bretelles). 401. Pas de
   * details. PO-Q1 9.K.11 : parité conceptuelle 9.K.10
   * `TENANTS_USER_AUTH_REQUIRED` (cohérence stricte 100% bilingue).
   */
  BROKERS_USER_AUTH_REQUIRED: {
    kind: "error",
    messages: {
      en: "User authentication required for this operation.",
      fr: "Authentification utilisateur requise pour cette opération.",
    },
  },

  // ===========================================================================
  // Chunk 9.Y.1 — Refus explicite des clés API sur les surfaces humaines
  //
  // Audit 9.Y.1 : `users.ts` et `form-templates.ts` montaient `requireAuth`
  // sans jamais exiger de scope api_key ni rejeter ce mode d'auth. Une clé
  // pouvait donc les atteindre avec N'IMPORTE quel scope — administration
  // d'utilisateurs et moteur de formulaires inclus.
  //
  // Décision PO 9.Y.1 : une clé API ne conserve que les opérations
  // explicitement permises par ses scopes. Ces deux surfaces n'en définissant
  // aucun, rien n'y est explicitement permis → refus.
  //
  // Parité de forme avec `TENANTS_USER_AUTH_REQUIRED` (9.K.10) et
  // `BROKERS_USER_AUTH_REQUIRED` (9.K.11). Wording neutre P-9 ADR 0027 : ne
  // distingue pas api_key d'agent, et n'annonce pas quel scope manquerait.
  // ===========================================================================

  /**
   * Toutes les routes `/v1/users/*` quand l'auth est une clé API. 403.
   * L'administration des utilisateurs exige un JWT humain portant le rôle
   * approprié — cf. décision PO 9.Y.1 §3. Pas de details.
   */
  USERS_USER_AUTH_REQUIRED: {
    kind: "error",
    messages: {
      en: "User authentication required for this operation.",
      fr: "Authentification utilisateur requise pour cette opération.",
    },
  },

  /**
   * Toutes les routes `/v1/form-templates/*` et `/v1/form-template-versions/*`
   * quand l'auth est une clé API. 403. Le moteur de formulaires est une API
   * privée broker (aucun outil MCP ne l'appelle) ; les écritures étaient déjà
   * bloquées par `assertHumanGated`, mais les lectures restaient ouvertes à
   * toute clé. Pas de details.
   */
  FORM_TEMPLATES_USER_AUTH_REQUIRED: {
    kind: "error",
    messages: {
      en: "User authentication required for this operation.",
      fr: "Authentification utilisateur requise pour cette opération.",
    },
  },

  // ===========================================================================
  // Chunk 9.N.1.A — Codes private intake-links (foundation F2 public intake)
  //
  // Couverture des routes privées broker `POST /v1/intake-links` et
  // `PATCH /v1/intake-links/:id/revoke` (apps/api/src/routes/intake-links.ts).
  // Convention 9.K.7 : préfixe par entité — `INTAKE_LINKS_*`. Granularité
  // PO-Q2 (un code par cas distinct).
  //
  // Hors scope 9.N.1.A (déférés 9.N.1.B) : codes routes publiques
  // (`PUBLIC_INTAKE_TOKEN_INVALID`, `_EXPIRED`, `_REVOKED`, `_RATE_LIMITED`,
  // `_HONEYPOT`, `_MIME_REJECTED`, `_QUOTA_EXCEEDED`). Le payload bilingue
  // public sera générique anti-fuite info (cadrage F2 §2 / §3.1).
  // ===========================================================================

  // --- INTAKE_LINKS (6 codes) ------------------------------------------------

  /**
   * Émis par POST `/v1/intake-links` quand le `project_id` du body ne
   * pointe sur aucun projet (UUID inconnu OU projet hors-tenant pour un
   * tenant_admin/broker). 404. `details.resource_id` = project_id reçu.
   * Distinct de `INTAKE_LINKS_PROJECT_TENANT_MISMATCH` car ici la cause est
   * "introuvable" (404), pas "scope cross-tenant détecté" (403).
   */
  INTAKE_LINKS_PROJECT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Project not found for intake link creation.",
      fr: "Projet introuvable pour la création du lien d'intake.",
    },
  },

  /**
   * Émis par POST `/v1/intake-links` quand un super_admin tente de créer
   * un lien via `x-tenant-id` pour un projet appartenant à un tenant ≠
   * (le projet existe mais pointe vers un autre tenant). 403. Garde-fou
   * super_admin pour empêcher un cross-tenant accidentel.
   */
  INTAKE_LINKS_PROJECT_TENANT_MISMATCH: {
    kind: "error",
    messages: {
      en: "Project does not belong to the resolved tenant.",
      fr: "Le projet n'appartient pas au tenant résolu.",
    },
  },

  /**
   * Émis par PATCH `/v1/intake-links/:id/revoke` quand le lien est
   * introuvable (UUID inexistant ou hors-tenant). 404.
   * `details.resource_id` porte l'UUID demandé.
   */
  INTAKE_LINKS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Intake link not found.",
      fr: "Lien d'intake introuvable.",
    },
  },

  /**
   * Émis par PATCH `/v1/intake-links/:id/revoke` quand le lien est déjà
   * révoqué (`revoked_at IS NOT NULL`). 409. Parité 9.K.7
   * `API_KEYS_ALREADY_REVOKED`.
   */
  INTAKE_LINKS_ALREADY_REVOKED: {
    kind: "error",
    messages: {
      en: "Intake link is already revoked.",
      fr: "Lien d'intake déjà révoqué.",
    },
  },

  /**
   * Émis par PATCH `/v1/intake-links/:id/revoke` quand le tenant_admin
   * ou broker tente de révoquer un lien d'un autre tenant. 403.
   * `details.context = "revoke"` pour cohérence avec les autres
   * `_TENANT_MISMATCH` du registre.
   */
  INTAKE_LINKS_TENANT_MISMATCH: {
    kind: "error",
    messages: {
      en: "Intake link belongs to another tenant.",
      fr: "Le lien d'intake appartient à un autre tenant.",
    },
  },

  /**
   * Émis par POST/PATCH `/v1/intake-links*` quand l'utilisateur n'a pas
   * un rôle autorisé (≠ `super_admin`, `tenant_admin`, `broker`). 403.
   * Les `client_contact` (et toute auth `api_key`) sont rejetés.
   * Cf. cadrage F2 §2 : « broker génère lien signé depuis son projet privé ».
   */
  INTAKE_LINKS_FORBIDDEN_ROLE: {
    kind: "error",
    messages: {
      en: "Reserved to brokers and tenant administrators.",
      fr: "Réservé aux courtiers et administrateurs tenant.",
    },
  },

  // ===========================================================================
  // Chunk 9.N.1.B — Codes public intake GET metadata + rate-limit générique
  //
  // 2 NOUVEAUX codes :
  //   - RATE_LIMITED                    : code générique transverse, parité
  //                                       9.K.5 globaux (`INTERNAL_ERROR`,
  //                                       `VALIDATION_ERROR`, etc.). Émis par
  //                                       `apps/api/src/middleware/rate-limit.ts`.
  //                                       Migration "résidu rate-limit legacy"
  //                                       → vrai bilingue (note D58.1 globale
  //                                       reste partielle, juste résidu nettoyé).
  //   - PUBLIC_INTAKE_LINK_UNAVAILABLE : émis par GET `/v1/public/intake/:token`
  //                                       pour TOUTE défaillance token-level
  //                                       (malformed/bad_signature/expired/
  //                                       revoked/link-not-found/quota-exhausted).
  //                                       Anti-leak unique (cadrage F2 §2 + §6
  //                                       risque "réponses différenciées low").
  //
  // Sécurité : pas de `details` exposé pour ces 2 codes.
  //   - `RATE_LIMITED` : header `retry-after` suffit côté client. Exposer
  //     `limit_per_minute` côté wire = info-disclosure du budget rate-limit
  //     (un attaquant pace ses attaques juste sous le seuil).
  //   - `PUBLIC_INTAKE_LINK_UNAVAILABLE` : la `reason` réelle (malformed vs
  //     expired vs revoked) reste loggué côté serveur uniquement (logger.info
  //     dans le route handler) — jamais projeté au wire.
  // ===========================================================================

  /**
   * Émis par `apps/api/src/middleware/rate-limit.ts` quand un bucket dépasse
   * sa limite (`opts.perMinute` ou `config.API_RATE_LIMIT_PER_MIN`). 429 +
   * header `retry-after` (en secondes, set juste avant le throw).
   *
   * **Pas de `details`** au wire : le budget rate-limit n'a pas à être leak.
   * Le client a `retry-after` pour savoir quand retry.
   *
   * Code transverse, parité 9.K.5 : pas de préfixe entité (cf. `INTERNAL_ERROR`,
   * `VALIDATION_ERROR`). Émis par tout caller du middleware (`/v1/auth/login`,
   * `/v1/auth/reset/request`, global `/v1/*`, `/v1/public/intake/*`).
   */
  RATE_LIMITED: {
    kind: "error",
    messages: {
      en: "Too many requests. Please retry later (see Retry-After header).",
      fr: "Trop de requêtes. Réessayez plus tard (cf. en-tête Retry-After).",
    },
  },

  /**
   * Émis par GET `/v1/public/intake/:token` pour toute défaillance token-level :
   * `malformed` / `bad_signature` / `expired` (HMAC) /
   * `link not found in DB` / `revoked_at IS NOT NULL` / `submission_count >= max_submissions` /
   * `project soft-deleted`.
   *
   * Anti-leak strict (cadrage F2 §2 + §6 "réponses différenciées low") :
   *   - Wire shape unique pour TOUS ces cas — un scanner ne peut pas
   *     distinguer "token forgé" vs "lien révoqué" vs "lien expiré" vs
   *     "lien inexistant" vs "quota atteint".
   *   - HTTP status 200 OK avec `body.ok = false` (anti-énumération côté
   *     status code aussi). Le route handler appelle `c.json(...)` direct
   *     plutôt que de throw via `bilingualHttpError` (qui émettrait 4xx).
   *   - Pas de `details` au wire (`reason` interne loggué serveur-side
   *     uniquement via `logger.info`).
   */
  PUBLIC_INTAKE_LINK_UNAVAILABLE: {
    kind: "error",
    messages: {
      en: "This intake link is unavailable. Please request a new link from the broker.",
      fr: "Ce lien d'intake n'est pas disponible. Demandez un nouveau lien à votre courtier.",
    },
  },

  // ===========================================================================
  // Chunk 9.N.1.B2 — Codes public intake POST (multipart upload + storage)
  //
  // 3 NOUVEAUX codes :
  //   - PUBLIC_INTAKE_INVALID_PAYLOAD  : Zod ou consent invalide (400)
  //   - PUBLIC_INTAKE_FILE_REJECTED    : structure multipart KO (count/taille)
  //                                      OU magic bytes KO (415)
  //   - PUBLIC_INTAKE_INTERNAL_ERROR   : storage upload fail / tx fail post-claim
  //                                      (500) — distinct du `INTERNAL_ERROR`
  //                                      générique pour traçabilité publique
  //
  // Anti-leak : `PUBLIC_INTAKE_LINK_UNAVAILABLE` (9.N.1.B) reste utilisé pour
  // TOUTE défaillance token-level + honeypot rempli + race quota
  // (parité GET / décision PO B2 — anti-énumération côté status code).
  //
  // Sécurité : pas de `details` exposés sur le wire pour ces codes (parité
  // 9.N.1.B). La cause précise reste loggué côté serveur uniquement.
  // ===========================================================================

  /**
   * Émis par POST `/v1/public/intake/:token` quand le body multipart est
   * structurellement invalide (Zod fail sur consent / comment trop long /
   * email mauvais format / consent.accepted ≠ true). 400.
   *
   * Distinct de `PUBLIC_INTAKE_LINK_UNAVAILABLE` (token-level) — ici le
   * token est valide mais le payload est cassé. Pas de `details` exposé
   * (Zod issues restent serveur-side via logger).
   */
  PUBLIC_INTAKE_INVALID_PAYLOAD: {
    kind: "error",
    messages: {
      en: "Invalid submission payload. Please verify the form and retry.",
      fr: "Données du formulaire invalides. Vérifiez et réessayez.",
    },
  },

  /**
   * Émis par POST `/v1/public/intake/:token` quand un ou plusieurs fichiers
   * sont rejetés (count > 10, taille > 25MB per-file, total > 100MB,
   * MIME hors whitelist, magic bytes mismatch, filename invalide). 415.
   *
   * Pas de `details` exposé (file index / reason exacte / MIME détecté
   * restent serveur-side). Le client final voit un message générique et
   * doit retry avec un fichier conforme.
   */
  PUBLIC_INTAKE_FILE_REJECTED: {
    kind: "error",
    messages: {
      en: "One or more files were rejected. Please verify file types and sizes.",
      fr: "Un ou plusieurs fichiers ont été rejetés. Vérifiez les formats et tailles.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.U.5 — MAGIC-LINK PUBLIC VERSIONNÉ
  // ---------------------------------------------------------------------------

  /**
   * Émis par POST `/v1/intake-links` (broker) quand `form_template_version_id`
   * pointe une version NON publiée. 409. Un lien versionné exige une version
   * publiée (immuable). Version inexistante/cross-tenant → 404
   * `FORM_TEMPLATE_VERSION_NOT_FOUND` (réutilisé, anti-leak) ; template archivé
   * → 409 `FORM_TEMPLATE_ARCHIVED` (réutilisé).
   */
  INTAKE_LINK_VERSION_NOT_PUBLISHED: {
    kind: "error",
    messages: {
      en: "This form version is not published. Publish it before linking it to an intake link.",
      fr: "Cette version de formulaire n'est pas publiée. Publiez-la avant de la lier à un lien d'intake.",
    },
  },

  /**
   * Émis par POST `/v1/public/intake/:token` (versionné) quand la même
   * `submission_id` (clé d'idempotence client) est réutilisée pour ce lien avec
   * des DONNÉES différentes (collision incohérente). 409. Aucune écriture, aucun
   * incrément de quota. Un retry identique (mêmes données) retourne l'original
   * (200), pas cette erreur. Pas de `details` exposé.
   */
  PUBLIC_INTAKE_IDEMPOTENCY_CONFLICT: {
    kind: "error",
    messages: {
      en: "This submission id was already used with different data. Use a new submission.",
      fr: "Cet identifiant de soumission a déjà été utilisé avec d'autres données. Recommencez une nouvelle soumission.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.U.3 — GÉNÉRATION D'UN MODÈLE DEPUIS UN DOCUMENT (IA)
  // ---------------------------------------------------------------------------
  // Écart assumé vs cadrage initial ADR 0028 Q7 (« soft-fail 200 {ok:false,
  // reason} », parité classifier 9.T.4) : la génération EST l'action demandée par
  // l'utilisateur (et non une étape interne d'une autre action), l'UI doit donc
  // afficher un message localisé → on s'aligne sur ADR 0027 (codes bilingues +
  // statut HTTP porteur). La sémantique soft-fail est conservée au sens strict :
  // jamais de 500 sur un échec métier ou fournisseur connu, jamais d'état partiel,
  // jamais de stack / texte du PDF / prompt dans la réponse.

  /**
   * Émis par POST `/v1/form-templates/generate-from-document` quand le
   * `source_file_id` est introuvable, appartient à un autre tenant, ou est
   * soft-deleted. 404 UNIQUE pour les trois cas (anti-énumération : le broker ne
   * doit pas distinguer « existe ailleurs » de « n'existe pas »).
   */
  FORM_TEMPLATE_GENERATION_FILE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Source document not found.",
      fr: "Document source introuvable.",
    },
  },

  /**
   * Fichier source non admissible : MIME ≠ application/pdf. 422. Les PDF scannés,
   * DOCX et XLSX sont hors scope 9.U.3 (born-digital uniquement, ADR Q9) — un
   * scanné passe la garde MIME puis échoue en `..._NO_TEXT`.
   */
  FORM_TEMPLATE_GENERATION_FORMAT_UNSUPPORTED: {
    kind: "error",
    messages: {
      en: "Only born-digital PDF documents can be used. Scanned images, Word and Excel files are not supported yet.",
      fr: "Seuls les PDF natifs (non numérisés) sont acceptés. Les images numérisées, Word et Excel ne sont pas encore pris en charge.",
    },
  },

  /**
   * PDF valide mais sans texte extractible utile (document numérisé, vide, ou
   * purement graphique). 422. Aucun appel LLM n'a été fait.
   */
  FORM_TEMPLATE_GENERATION_NO_TEXT: {
    kind: "error",
    messages: {
      en: "No readable text could be extracted from this PDF. It is likely a scan; OCR is not supported yet.",
      fr: "Aucun texte lisible n'a pu être extrait de ce PDF. Il s'agit probablement d'une numérisation ; la reconnaissance de texte n'est pas encore prise en charge.",
    },
  },

  /**
   * Document hors bornes serveur (pages / taille). 422. `details` ne contient que
   * les bornes et les compteurs — jamais un extrait du document.
   */
  FORM_TEMPLATE_GENERATION_TOO_LARGE: {
    kind: "error",
    messages: {
      en: "This document is too large to be analyzed. Split it or use a shorter form.",
      fr: "Ce document est trop volumineux pour être analysé. Divisez-le ou utilisez un formulaire plus court.",
    },
  },

  /**
   * Sortie du modèle inexploitable : JSON invalide, schéma non conforme au contrat
   * brouillon, ou hors des bornes de génération (sections/champs/options). 422.
   * `details` ne contient que des chemins/compteurs Zod — jamais la sortie brute.
   */
  FORM_TEMPLATE_GENERATION_INVALID_OUTPUT: {
    kind: "error",
    messages: {
      en: "The generated form could not be used. Try again or create the form manually.",
      fr: "Le formulaire généré n'a pas pu être exploité. Réessayez ou créez le formulaire manuellement.",
    },
  },

  /**
   * Le modèle n'a pas répondu dans le délai imparti (30 s). 503. Aucun template
   * n'a été créé (l'appel a lieu HORS transaction) — un retry avec la MÊME clé
   * d'idempotence est sûr.
   */
  FORM_TEMPLATE_GENERATION_TIMEOUT: {
    kind: "error",
    messages: {
      en: "Generation took too long. Please try again.",
      fr: "La génération a pris trop de temps. Réessayez.",
    },
  },

  /**
   * Fournisseur LLM indisponible / erreur non récupérable après retries. 503.
   * Aucun template créé. Distinct de `INTERNAL_ERROR` pour le monitoring.
   */
  FORM_TEMPLATE_GENERATION_UNAVAILABLE: {
    kind: "error",
    messages: {
      en: "The generation service is temporarily unavailable. Please try again shortly.",
      fr: "Le service de génération est temporairement indisponible. Réessayez dans un instant.",
    },
  },

  /**
   * Même `generation_request_id` réutilisée avec une demande DIFFÉRENTE
   * (empreinte canonique `source_file_id | langue | nom normalisé` divergente).
   * 409. Aucun appel LLM, aucune écriture. Un retry IDENTIQUE retourne l'original
   * (200), jamais cette erreur — la collision n'est jamais masquée en rejeu.
   */
  /**
   * Le fournisseur LLM refuse DÉFINITIVEMENT la requête pour une raison de
   * configuration opérateur : solde/crédit épuisé, clé invalide, permission
   * refusée (HTTP 400 « credit balance too low », 401, 403). 503.
   *
   * Distinct de `…_UNAVAILABLE` (429/529/5xx transitoires) : ici **réessayer ne
   * sert à rien** — un administrateur doit intervenir. Constaté en smoke réel
   * 9.W.1, où un 400 « credit balance is too low » était rapporté comme une
   * indisponibilité temporaire invitant à réessayer.
   *
   * Aucun message brut du fournisseur n'est exposé ni journalisé (seul le `type`
   * d'erreur, enum fermé, l'est côté serveur).
   */
  FORM_TEMPLATE_GENERATION_PROVIDER_CONFIGURATION_ERROR: {
    kind: "error",
    messages: {
      en: "Form generation is unavailable due to an AI provider configuration issue. Contact an administrator — retrying will not help.",
      fr: "La génération de formulaire est indisponible en raison d'un problème de configuration du fournisseur d'IA. Contactez un administrateur — réessayer ne changera rien.",
    },
  },

  FORM_TEMPLATE_GENERATION_IDEMPOTENCY_CONFLICT: {
    kind: "error",
    messages: {
      en: "This generation request id was already used with a different document or settings. Start a new generation.",
      fr: "Cet identifiant de génération a déjà été utilisé avec un autre document ou d'autres paramètres. Lancez une nouvelle génération.",
    },
  },

  // ===========================================================================
  // 9.W.2 — intégrité et confinement des voies documentaires
  // ===========================================================================

  /**
   * Émis par `POST /v1/intake-events` (upload broker multipart) quand un fichier
   * n'est pas un PDF.
   *
   * Le validateur partagé acceptait les 8 types de la whitelist publique
   * (PNG/JPEG/WebP/DOCX/XLSX/CSV/TXT), alors que **tout le pipeline aval est
   * conçu pour du PDF** : classifier vision, extraction, comptage de pages.
   * Un PNG entrait donc dans le flux pour n'y produire que des échecs.
   *
   * Le message reste volontairement générique sur la cause exacte du rejet
   * (type déclaré vs contenu réel) — le détail va dans les logs serveur.
   */
  INTAKE_EVENTS_UPLOAD_PDF_ONLY: {
    kind: "error",
    messages: {
      en: "Only PDF files are accepted here. Convert the document to PDF and upload it again.",
      fr: "Seuls les fichiers PDF sont acceptés ici. Convertissez le document en PDF puis téléversez-le à nouveau.",
    },
  },

  /**
   * Émis par `POST /v1/form-responses` (Chunk 9.W.2) quand `client_draft_id` a
   * déjà été utilisé par le même utilisateur pour un brouillon **différent**
   * (autre version de modèle, ou données initiales différentes).
   *
   * Un rejeu strictement identique renvoie 200 avec le brouillon existant ;
   * seule une réutilisation incohérente de la clé produit ce conflit.
   */
  FORM_RESPONSE_DRAFT_IDEMPOTENCY_CONFLICT: {
    kind: "error",
    messages: {
      en: "This draft key was already used with a different form version or different initial data. Start a new draft.",
      fr: "Cette clé de brouillon a déjà été utilisée avec une autre version de formulaire ou d'autres données initiales. Créez un nouveau brouillon.",
    },
  },

  /**
   * Émis par `DELETE /v1/form-responses/:id` (Chunk 9.W.2) quand la réponse
   * existe mais n'est pas abandonnable : déjà soumise, verrouillée, rattachée à
   * un intake (donc à une trace client), ou appartenant à quelqu'un d'autre.
   *
   * **Un seul code pour tous ces cas, délibérément** : distinguer « pas à vous »
   * de « déjà soumise » renseignerait un appelant sur l'existence et l'état de
   * ressources qui ne le concernent pas.
   */
  FORM_RESPONSE_NOT_DISCARDABLE: {
    kind: "error",
    messages: {
      en: "This draft cannot be discarded. Only your own unsubmitted drafts, not linked to a client submission, can be discarded.",
      fr: "Ce brouillon ne peut pas être abandonné. Seuls vos propres brouillons non soumis, non rattachés à une soumission client, peuvent l'être.",
    },
  },

  /**
   * Émis par POST `/v1/public/intake/:token` sur erreur infra inattendue
   * (storage upload fail, DB tx fail post-claim, cleanup partiel). 500.
   *
   * Distinct du `INTERNAL_ERROR` 9.K.5 générique pour faciliter le filtrage
   * des erreurs publiques en monitoring (taux de panne du POST public est
   * une métrique critique de la surface F2).
   *
   * Pas de stack/dev_message exposé même en `isDev` (anti-leak strict —
   * différent de INTERNAL_ERROR qui peut exposer dev_message via PO-Q2 9.K.5).
   */
  PUBLIC_INTAKE_INTERNAL_ERROR: {
    kind: "error",
    messages: {
      en: "An internal error occurred while processing the submission. Please retry shortly.",
      fr: "Une erreur interne est survenue lors du traitement. Réessayez dans quelques instants.",
    },
  },

  // ===========================================================================
  // 9.Q.2 — Comparison Notes MVP (ADR 0026 axe C Phase 1)
  // ===========================================================================
  //
  // 5 codes pour routes `/v1/comparison-notes` + nested `/v1/projects/:id/
  // comparison-notes`. Routes humain-only Phase 1 (api_key rejetée handler-side).
  // Anti-leak strict : zéro `details` projeté wire-side pour ces codes.

  /**
   * Émis par GET/PATCH/DELETE quand l'id n'existe pas OU cross-tenant (RLS).
   * 404 anti-leak unique (parité INTAKE_LINKS_NOT_FOUND / AGENT_IDENTITIES_NOT_FOUND).
   */
  COMPARISON_NOTES_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Comparison note not found.",
      fr: "Note de comparaison introuvable.",
    },
  },

  /**
   * Émis par POST quand le project_id fourni n'existe pas OU est dans un autre
   * tenant. 404 anti-leak unique (parité PROJECTS_NOT_FOUND wording).
   */
  COMPARISON_NOTES_PROJECT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Project not found.",
      fr: "Projet introuvable.",
    },
  },

  /**
   * Émis par POST quand `submission_id` fourni est cross-project (la submission
   * existe mais n'appartient pas au `project_id` de la note). 422 (semantic
   * validation post-Zod, pré-INSERT).
   */
  COMPARISON_NOTES_SUBMISSION_PROJECT_MISMATCH: {
    kind: "error",
    messages: {
      en: "Submission does not belong to the specified project.",
      fr: "La soumission n'appartient pas au projet spécifié.",
    },
  },

  /**
   * Émis par POST quand le combo scope est invalide :
   *   field_key NULL + submission_id NOT NULL (= "toutes fields d'une submission",
   *   sémantique hors spec Phase 1).
   *
   * 422 (semantic validation pré-INSERT). La CHECK DB `comparison_notes_scope_chk`
   * est garde-fou — l'erreur ne devrait pas rebondir DB-side via UI valide.
   */
  COMPARISON_NOTES_SCOPE_INVALID: {
    kind: "error",
    messages: {
      en: "Invalid scope combination: field_key is required when submission_id is set.",
      fr: "Combinaison de portée invalide : field_key requis quand submission_id est fourni.",
    },
  },

  /**
   * Émis par POST/PATCH/DELETE quand `auth.kind === "api_key"` (Phase 1 humain
   * only entièrement). 403 anti-leak — wording neutre (parité
   * EXTRACTIONS_OVERRIDE_USER_ONLY P-9 ADR 0027) qui ne distingue pas api_key vs
   * agent vs auth absente.
   */
  COMPARISON_NOTES_FORBIDDEN_USER_ONLY: {
    kind: "error",
    messages: {
      en: "This action requires a human user.",
      fr: "Cette action requiert un utilisateur humain.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.V.1 — Registre assureurs (ADR 0030). CRUD + activation, no hard delete.
  // ---------------------------------------------------------------------------
  INSURERS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Insurer not found.",
      fr: "Assureur introuvable.",
    },
  },
  INSURERS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },
  INSURERS_CODE_DUPLICATE: {
    kind: "error",
    messages: {
      en: "An insurer with this code already exists. Choose a different code.",
      fr: "Un assureur avec ce code existe déjà. Choisissez un code différent.",
    },
  },
  /**
   * Écritures structurelles (create/update/activate/deactivate) réservées à
   * tenant_admin ou super_admin. Un broker/client_contact reçoit ce 403.
   */
  INSURERS_FORBIDDEN_ADMIN_ONLY: {
    kind: "error",
    messages: {
      en: "Reserved to tenant admins or super admins.",
      fr: "Réservé aux administrateurs tenant ou super_admin.",
    },
  },
  /**
   * Écritures structurelles refusées aux api_keys même avec le scope
   * `insurers:write`. Wording neutre anti-leak (parité COMPARISON_NOTES P-9).
   */
  INSURERS_FORBIDDEN_USER_ONLY: {
    kind: "error",
    messages: {
      en: "This action requires a human user.",
      fr: "Cette action requiert un utilisateur humain.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.V.2 — Carnet de contacts assureur (ADR 0030 §5). CRUD nested, no hard delete.
  // ---------------------------------------------------------------------------
  INSURER_CONTACTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Insurer contact not found.",
      fr: "Contact assureur introuvable.",
    },
  },
  INSURER_CONTACTS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },
  /**
   * Création/activation d'un contact (ou email) actif refusée quand l'assureur
   * parent est inactif — réactiver l'assureur d'abord (9.V.2 garde-fou).
   */
  INSURER_CONTACTS_INSURER_INACTIVE: {
    kind: "error",
    messages: {
      en: "The insurer is inactive. Reactivate it before adding or enabling contacts.",
      fr: "L'assureur est inactif. Réactivez-le avant d'ajouter ou d'activer des contacts.",
    },
  },
  INSURER_CONTACT_EMAILS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Insurer contact email not found.",
      fr: "Courriel de contact assureur introuvable.",
    },
  },
  INSURER_CONTACT_EMAILS_NO_FIELDS_TO_UPDATE: {
    kind: "error",
    messages: {
      en: "No fields to update.",
      fr: "Aucun champ à mettre à jour.",
    },
  },
  /**
   * Un email actif identique existe déjà pour cet assureur (unicité
   * case-insensitive par assureur, ADR 0030 §5). Vaut aussi à la réactivation.
   */
  INSURER_CONTACT_EMAILS_DUPLICATE: {
    kind: "error",
    messages: {
      en: "An active email with this address already exists for this insurer.",
      fr: "Un courriel actif avec cette adresse existe déjà pour cet assureur.",
    },
  },
  /**
   * Remplaçant de contact principal invalide à la désactivation : inexistant,
   * inactif, identique à la cible, ou hors de l'assureur (9.V.2b).
   */
  INSURER_CONTACTS_REPLACEMENT_INVALID: {
    kind: "error",
    messages: {
      en: "The chosen replacement contact is invalid: it must be a different, active contact of the same insurer.",
      fr: "Le contact de remplacement choisi est invalide : il doit être un contact actif, distinct, du même assureur.",
    },
  },
  /**
   * Remplaçant d'email principal invalide à la désactivation : inexistant,
   * inactif, identique à la cible, ou hors du contact parent (9.V.2b).
   */
  INSURER_CONTACT_EMAILS_REPLACEMENT_INVALID: {
    kind: "error",
    messages: {
      en: "The chosen replacement email is invalid: it must be a different, active email of the same contact.",
      fr: "Le courriel de remplacement choisi est invalide : il doit être un courriel actif, distinct, du même contact.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.V.2c — résolution insurer_code → app.insurers (recipients, extraction).
  // ---------------------------------------------------------------------------
  /** Code assureur absent du registre du tenant (résolution pour une nouvelle opération). */
  INSURER_CODE_UNKNOWN: {
    kind: "error",
    messages: {
      en: "Unknown insurer code for this tenant. Add the insurer to the registry first.",
      fr: "Code assureur inconnu pour ce cabinet. Ajoutez l'assureur au registre d'abord.",
    },
  },
  /** Code assureur présent mais inactif — non sélectionnable pour une nouvelle opération. */
  INSURER_CODE_INACTIVE: {
    kind: "error",
    messages: {
      en: "This insurer is inactive and cannot be selected for a new operation.",
      fr: "Cet assureur est inactif et ne peut pas être sélectionné pour une nouvelle opération.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.V.2c-S1 — durcissement isolation/intégrité pré-merge (audit Codex PR #18).
  // ---------------------------------------------------------------------------
  /**
   * #2 — L'adresse d'un destinataire ne peut plus être modifiée : la tentative
   * d'envoi outbox est finalisée (sent|failed|expired) et aucune row `queued`
   * n'existe. L'historique terminal n'est jamais réécrit.
   */
  QUOTE_REQUEST_RECIPIENTS_EMAIL_OUTBOX_FINALIZED: {
    kind: "error",
    messages: {
      en: "The recipient email can no longer be changed because the delivery attempt is finalized.",
      fr: "L'adresse du destinataire ne peut plus être modifiée parce que la tentative d'envoi est finalisée.",
    },
  },
  /** #4 — Promotion principal refusée : le contact cible n'est pas actif. */
  INSURER_CONTACTS_INACTIVE_NOT_PRIMARY: {
    kind: "error",
    messages: {
      en: "An inactive contact cannot be set as primary. Reactivate it first.",
      fr: "Un contact inactif ne peut pas être défini comme principal. Réactivez-le d'abord.",
    },
  },
  /** #4 — Promotion principal refusée : l'email cible n'est pas actif. */
  INSURER_CONTACT_EMAILS_INACTIVE_NOT_PRIMARY: {
    kind: "error",
    messages: {
      en: "An inactive email cannot be set as primary. Reactivate it first.",
      fr: "Un courriel inactif ne peut pas être défini comme principal. Réactivez-le d'abord.",
    },
  },
  /**
   * #5 — Combinaison pass_id ↔ source.kind incohérente : un pass d'intake sur
   * une source submission (ou un pass legacy sur un intake_event) est rejeté
   * AVANT tout choix de gate (anti-contournement d'autorisation).
   */
  EXTRACTIONS_PASS_SOURCE_MISMATCH: {
    kind: "error",
    messages: {
      en: "This extraction pass is not allowed for this source kind.",
      fr: "Cette passe d'extraction n'est pas autorisée pour ce type de source.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.V.2c-S1.1 — durable delivery claim + invariant closure (audit Codex PR #18).
  // ---------------------------------------------------------------------------
  /**
   * #2 (S1.1) — L'adresse d'un destinataire ne peut pas être modifiée pendant
   * qu'une tentative d'envoi est claimée/en vol (lease worker vivant). Distinct
   * de `…OUTBOX_FINALIZED` (terminal) : ici la tentative n'est pas finalisée,
   * réessayer sous peu.
   */
  QUOTE_REQUEST_RECIPIENTS_EMAIL_SEND_IN_PROGRESS: {
    kind: "error",
    messages: {
      en: "The recipient email cannot be changed while delivery is in progress. Try again shortly.",
      fr: "L'adresse du destinataire ne peut pas être modifiée pendant qu'un envoi est en cours. Réessayez dans quelques instants.",
    },
  },
  /**
   * #4 (S1.1) — Un `pass_id` explicite (parmi les passes intake) est obligatoire
   * pour une source `intake_event` — aucun fallback `identification` (legacy
   * submission) n'est appliqué à un intake_event.
   */
  EXTRACTIONS_PASS_REQUIRED: {
    kind: "error",
    messages: {
      en: "An extraction pass must be specified for an intake event.",
      fr: "Un type d'extraction doit être précisé pour un événement d'admission.",
    },
  },

  // --- FORM TEMPLATES (F2-Forms-1b-i / 9.U.2 — 8 codes) ----------------------

  /** GET/PATCH/DELETE d'un modèle introuvable ou hors tenant. 404. */
  FORM_TEMPLATE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "This form template was not found.",
      fr: "Ce modèle de formulaire est introuvable.",
    },
  },

  /** POST create avec un `code` déjà utilisé sur le tenant (unique). 409.
   * `details.code` porte la valeur en conflit (broker connaît ses propres codes). */
  FORM_TEMPLATE_CODE_DUPLICATE: {
    kind: "error",
    messages: {
      en: "A form template with this code already exists.",
      fr: "Un modèle de formulaire avec ce code existe déjà.",
    },
  },

  /** Version de modèle introuvable ou hors tenant. 404. */
  FORM_TEMPLATE_VERSION_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "This form template version was not found.",
      fr: "Cette version de modèle est introuvable.",
    },
  },

  /** Mutation (métadonnées / nouvelle version / édition brouillon / publication)
   * demandée sur un modèle archivé. 409. */
  FORM_TEMPLATE_ARCHIVED: {
    kind: "error",
    messages: {
      en: "This form template is archived and cannot be modified.",
      fr: "Ce modèle est archivé et ne peut pas être modifié.",
    },
  },

  /** POST /versions alors qu'un brouillon actif existe déjà (un seul autorisé).
   * 409. `details.draft_version_id` porte l'id du brouillon existant (tenant-scopé). */
  FORM_VERSION_ACTIVE_DRAFT_EXISTS: {
    kind: "error",
    messages: {
      en: "An active draft version already exists for this template.",
      fr: "Un brouillon actif existe déjà pour ce modèle.",
    },
  },

  /** PATCH du schema/changelog d'une version déjà publiée (immuable). 409. */
  FORM_RESPONSE_EDIT_CONFLICT: {
    kind: "error",
    messages: {
      fr: "Cette réponse a changé dans une autre session. Votre saisie est conservée. Rouvrez le brouillon pour comparer avant de sauvegarder.",
      en: "This response changed in another session. Your input is preserved. Reopen the draft to compare before saving.",
    },
  },

  FORM_VERSION_EDIT_CONFLICT: {
    kind: "error",
    messages: {
      fr: "Ce brouillon a été modifié depuis votre lecture. Vos modifications n’ont pas été enregistrées. Consultez la version récente avant de reprendre.",
      en: "This draft has changed since you opened it. Your changes were not saved. Review the latest version before continuing.",
    },
  },

  FORM_VERSION_PUBLISHED_IMMUTABLE: {
    kind: "error",
    messages: {
      en: "This version is published and immutable.",
      fr: "Cette version est publiée et immuable.",
    },
  },

  /** POST /publish d'une version déjà publiée. 409. */
  FORM_VERSION_ALREADY_PUBLISHED: {
    kind: "error",
    messages: {
      en: "This version is already published.",
      fr: "Cette version est déjà publiée.",
    },
  },

  /** POST /publish d'une version sans section/champ utilisable (contrat strict). 422. */
  QUOTE_REQUEST_SEND_MANDATE_ARTIFACT_MISSING: {
    kind: "error",
    messages: {
      fr: "Le document signé du mandat est indisponible. Rétablissez son accès avant de préparer l’envoi.",
      en: "The signed mandate document is unavailable. Restore access before preparing the send.",
    },
  },
  QUOTE_REQUEST_SEND_CENSUS_REQUIRED: {
    kind: "error",
    messages: {
      fr: "Ajoutez l’effectif des employés au dossier avant d’envoyer la demande.",
      en: "Add the employee census to the file before sending the request.",
    },
  },
  QUOTE_REQUEST_SEND_CENSUS_NOT_LOCKED: {
    kind: "error",
    messages: {
      fr: "Verrouillez l’effectif des employés puis produisez son export avant l’envoi.",
      en: "Lock the employee census and generate its export before sending.",
    },
  },
  QUOTE_REQUEST_SEND_COMMISSION_REQUIRED: {
    kind: "error",
    messages: {
      fr: "Renseignez le taux de commission demandé dans le dossier avant l’envoi.",
      en: "Enter the requested commission rate in the file before sending.",
    },
  },
  QUOTE_REQUEST_SEND_ATTACHMENT_MISSING: {
    kind: "error",
    messages: {
      fr: "Une pièce à transmettre est indisponible. Vérifiez les documents avant l’envoi.",
      en: "An attachment is unavailable. Check the documents before sending.",
    },
  },
  FORM_PURPOSE_REQUIRED: {
    kind: "error",
    messages: {
      fr: "Choisissez l’usage de cette version avant de la publier.",
      en: "Choose the purpose of this version before publishing it.",
    },
  },
  FORM_MANDATE_CLIENT_ONLY: {
    kind: "error",
    messages: {
      fr: "Ce mandat doit être signé par le client. Envoyez-lui une invitation depuis le dossier.",
      en: "This mandate must be signed by the client. Send an invitation from the file.",
    },
  },
  QUOTE_REQUEST_SPEC_PURPOSE_INVALID: {
    kind: "error",
    messages: {
      fr: "Choisissez une version publiée et approuvée comme cahier de charges. Un mandat ne peut pas servir de cahier.",
      en: "Choose a published version approved as a business specification. A mandate cannot be used as a specification.",
    },
  },
  FORM_VERSION_EMPTY: {
    kind: "error",
    messages: {
      en: "A form version must have at least one section and one usable field to be published.",
      fr: "Une version doit comporter au moins une section et un champ utilisable pour être publiée.",
    },
  },

  // --- FORM RESPONSES (F2-Forms-1b-ii / 9.U.2 — 7 codes) ---------------------

  /** GET/PATCH/submit/lock d'une réponse introuvable ou hors tenant. 404. */
  FORM_RESPONSE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "This form response was not found.",
      fr: "Cette réponse de formulaire est introuvable.",
    },
  },

  /** PATCH `data` d'une réponse qui n'est plus un brouillon (soumise ou verrouillée). 409. */
  FORM_RESPONSE_NOT_EDITABLE: {
    kind: "error",
    messages: {
      en: "This response can no longer be edited.",
      fr: "Cette réponse ne peut plus être modifiée.",
    },
  },

  /** POST /submit d'une réponse déjà soumise. 409. */
  FORM_RESPONSE_ALREADY_SUBMITTED: {
    kind: "error",
    messages: {
      en: "This response has already been submitted.",
      fr: "Cette réponse a déjà été soumise.",
    },
  },

  /** POST /lock d'une réponse encore au statut brouillon (soumission requise d'abord). 409. */
  FORM_RESPONSE_NOT_SUBMITTED: {
    kind: "error",
    messages: {
      en: "This response must be submitted before it can be locked.",
      fr: "Cette réponse doit être soumise avant de pouvoir être verrouillée.",
    },
  },

  /** POST /submit ou /lock d'une réponse déjà verrouillée (état terminal). 409. */
  FORM_RESPONSE_ALREADY_LOCKED: {
    kind: "error",
    messages: {
      en: "This response is locked and can no longer change.",
      fr: "Cette réponse est verrouillée et ne peut plus changer.",
    },
  },

  /** POST create d'une réponse rattachée à une version non publiée (brouillon). 409. */
  FORM_RESPONSE_VERSION_NOT_PUBLISHED: {
    kind: "error",
    messages: {
      en: "A response can only target a published form version.",
      fr: "Une réponse ne peut cibler qu'une version de formulaire publiée.",
    },
  },

  /** create/PATCH/submit dont `data` ne respecte pas le schema de la version liée. 422.
   * `details.issues` porte des chemins/types d'erreur non sensibles (jamais le schema
   * interne complet, ni SQL/contraintes/stack). */
  FORM_RESPONSE_INVALID_DATA: {
    kind: "error",
    messages: {
      en: "The submitted data does not match this form version.",
      fr: "Les données soumises ne correspondent pas à cette version du formulaire.",
    },
  },

  // --- FORM RESPONSES — submit-to-intake (F2-Forms-1d-i / 9.U.6 — 3 codes) ----

  /** `POST /:id/submit-to-intake` avec un `project_id` introuvable ou hors tenant. 404
   * anti-leak (jamais révéler l'existence cross-tenant). */
  FORM_RESPONSE_PROJECT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "The destination project was not found.",
      fr: "Le projet de destination est introuvable.",
    },
  },

  /** Retry `submit-to-intake` sur une réponse déjà liée à un intake event, avec un
   * `project_id` DIFFÉRENT de celui de l'event existant. 409 (jamais retourner
   * silencieusement un event rattaché à un autre projet). */
  FORM_RESPONSE_INTAKE_PROJECT_MISMATCH: {
    kind: "error",
    messages: {
      en: "This response is already linked to an intake event for a different project.",
      fr: "Cette réponse est déjà liée à un intake event pour un autre projet.",
    },
  },

  /** Invariant violé : réponse `submitted`/`locked` SANS `intake_event_id` rencontrée
   * par `submit-to-intake`. 409 déterministe — jamais de création silencieuse d'event. */
  FORM_RESPONSE_SUBMITTED_WITHOUT_INTAKE: {
    kind: "error",
    messages: {
      en: "This response is in an inconsistent state (submitted without a linked intake event).",
      fr: "Cette réponse est dans un état incohérent (soumise sans intake event lié).",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.AA.1a — orchestration des passes d'extraction métier
  // ---------------------------------------------------------------------------

  /**
   * Refus de toute identité non humaine sur les routes d'orchestration.
   *
   * Wording neutre (parité `EXTRACTIONS_OVERRIDE_USER_ONLY` P-9 ADR 0027) : ne
   * distingue ni clé API, ni agent, ni absence d'authentification. Lancer une
   * orchestration engage 6 appels LLM payants — décision PO 9.AA.1 §3 : humains
   * autorisés uniquement.
   */
  EXTRACTION_ORCHESTRATIONS_USER_ONLY: {
    kind: "error",
    messages: {
      en: "Launching an extraction run must be performed by a human user.",
      fr: "Le lancement d'une extraction doit être effectué par un utilisateur humain.",
    },
  },

  /**
   * Une orchestration est déjà en cours sur cette soumission.
   *
   * 409 déterministe, avec `details.orchestration_id` pour que l'appelant puisse
   * suivre celle qui tourne plutôt que d'en relancer une. C'est la traduction du
   * `unique_violation` sur `uq_extraction_orchestrations_active` (migration 0067)
   * — donc une garantie du moteur, pas une vérification applicative gagnable à la
   * course.
   */
  EXTRACTION_ORCHESTRATIONS_ALREADY_ACTIVE: {
    kind: "error",
    messages: {
      en: "An extraction run is already in progress for this submission.",
      fr: "Une extraction est déjà en cours pour cette soumission.",
    },
  },

  /** Orchestration inexistante, ou hors du tenant de l'appelant (même réponse). */
  EXTRACTION_ORCHESTRATIONS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Extraction run not found.",
      fr: "Extraction introuvable.",
    },
  },

  /**
   * Le plan ne peut pas être calculé : la passe `identification` n'a pas encore
   * produit `sections_present` pour cette soumission.
   *
   * Le message porte l'action suivante — exigence microcopy du repo : une erreur
   * dit quoi faire, pas seulement ce qui manque.
   */
  /**
   * Le fichier visé n'est pas celui sur lequel l'identification a lu les sections.
   *
   * Remarque P2 Codex sur 9.AA.1b. Le plan décrit un document précis ; lancer les
   * passes sur un autre ferait extraire le mauvais — une garantie du nouveau
   * document pourrait être sautée, et une passe payée pour une garantie absente.
   *
   * Le message porte l'action suivante, comme l'exige la doctrine microcopy.
   */
  EXTRACTION_ORCHESTRATIONS_FILE_MISMATCH: {
    kind: "error",
    messages: {
      en: "This plan was built from a different document. Run the identification pass on the file you selected before extracting its coverages.",
      fr: "Ce plan a été établi à partir d'un autre document. Lancez l'identification sur le fichier que vous avez sélectionné avant d'en extraire les garanties.",
    },
  },

  EXTRACTION_ORCHESTRATIONS_IDENTIFICATION_REQUIRED: {
    kind: "error",
    messages: {
      en: "Run the identification pass on this submission first — it detects which sections the document contains.",
      fr: "Lancez d'abord la passe d'identification sur cette soumission : c'est elle qui détecte les sections présentes dans le document.",
    },
  },

  // --- PROJECT REQUIREMENTS / COMPLETENESS (chunk 9.AC.1 — 5 codes) -----------

  /**
   * Émis par `GET|POST /v1/project-requirements` et
   * `GET /v1/projects/:id/completeness` quand le requirement demandé est
   * introuvable OU appartient à un autre tenant. 404.
   *
   * **Anti-leak unique** (parité `DOCUMENT_LINKS_FILE_NOT_FOUND`) : un seul code
   * pour not_found et cross-tenant. Signaler à un courtier qu'un requirement
   * existe chez un autre tenant lui apprendrait qu'un dossier concurrent existe.
   */
  PROJECT_REQUIREMENTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Requirement not found.",
      fr: "Exigence introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/project-requirements` quand `requirement_key` ne
   * correspond pas à la forme attendue pour le `kind` donné. 422.
   *
   * Les cinq formes :
   *   `form_response` → un UUID de `form_template_versions`
   *   `company_field` → une colonne de la liste blanche `COMPANY_REQUIREMENT_FIELDS`
   *   `contact_ref`   → exactement `'project.contact_id'`
   *   `document`      → un `link_role` non blanc, 1..80 caractères
   *   `manual`        → texte libre non blanc
   *
   * `details.kind` et `details.requirement_key` sont projetés : ce sont les
   * valeurs que le courtier vient d'envoyer, il les connaît déjà. Sans elles, il
   * ne saurait pas LAQUELLE des cinq formes il a ratée.
   */
  PROJECT_REQUIREMENTS_INVALID_KEY: {
    kind: "error",
    messages: {
      en: "This requirement reference does not match the expected form for its kind.",
      fr: "Cette référence d\u2019exigence ne correspond pas à la forme attendue pour sa nature.",
    },
  },

  /**
   * Émis par POST `/v1/project-requirements` quand le même
   * `(project_id, kind, requirement_key)` est déjà déclaré et vivant. 409.
   *
   * C'est la remontée applicative de `uq_project_requirements_live` : deux clics
   * sur « ajouter » produiraient deux manquants pour la même chose, donc un
   * compteur qui mentirait au conseiller.
   *
   * Aucun `details` : l'identifiant du requirement existant n'apporte rien au
   * courtier, qui voit déjà la ligne dans sa liste.
   */
  PROJECT_REQUIREMENTS_ALREADY_DECLARED: {
    kind: "error",
    messages: {
      en: "This requirement is already declared on this project.",
      fr: "Cette exigence est déjà déclarée sur ce dossier.",
    },
  },

  /**
   * Émis par POST `/v1/project-requirements` avec `kind: 'form_response'` quand
   * la version de formulaire référencée est introuvable, appartient à un autre
   * tenant, ou n'est pas publiée. 422.
   *
   * Exiger une version non publiée créerait un dead-end : personne ne peut y
   * répondre, donc le dossier ne pourrait jamais devenir prêt.
   */
  PROJECT_REQUIREMENTS_FORM_VERSION_UNAVAILABLE: {
    kind: "error",
    messages: {
      en: "This form version cannot be required: it does not exist or is not published.",
      fr: "Cette version de formulaire ne peut pas être exigée : elle n\u2019existe pas ou n\u2019est pas publiée.",
    },
  },

  /**
   * Émis par POST `/v1/missing-information` quand le projet ciblé est
   * introuvable ou appartient à un autre tenant. 404. Anti-leak unique.
   */
  MISSING_INFORMATION_PROJECT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Project not found.",
      fr: "Dossier introuvable.",
    },
  },

  /**
   * Émis par POST `/v1/intake-links` (9.AC.1) quand la version de formulaire
   * choisie contient un champ REQUIS qu'un client public ne peut pas remplir —
   * `company_ref` ou `contact_ref`. 422.
   *
   * Sans cette garde, on créait le lien, on l'envoyait au client, et le
   * cul-de-sac se découvrait chez lui : la validation `mode: "submit"` exige le
   * champ, le renderer public ne peut pas le saisir, donc rien n'est jamais
   * soumis et le dossier ne devient jamais prêt.
   *
   * Ces deux types restent volontairement en lecture seule côté public : les
   * rendre saisissables exigerait de lister les entreprises ou contacts du
   * cabinet à un visiteur anonyme, c'est-à-dire d'offrir le portefeuille client à
   * qui possède un lien.
   *
   * `details.fields` porte les clés fautives — le courtier a besoin de savoir
   * LESQUELLES corriger dans son modèle.
   */
  /**
   * 404 — le destinataire nommé pour un lien nominatif est introuvable.
   *
   * Volontairement **indistinct** : destinataire inexistant, d'un autre cabinet,
   * ou d'un autre dossier rendent tous le même refus. Distinguer renseignerait
   * un appelant sur ce qui existe ailleurs.
   */
  INTAKE_LINKS_RECIPIENT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "This recipient cannot be used for this link.",
      fr: "Ce destinataire ne peut pas être utilisé pour ce lien.",
    },
  },

  /**
   * 409 — le destinataire existe et appartient bien au dossier, mais son
   * créneau n'est pas disponible : demande close, destinataire déjà terminal,
   * ou proposition déjà rattachée. Émettre un lien nominatif vers lui
   * promettrait une provenance qu'on ne pourrait pas honorer.
   */
  INTAKE_LINKS_RECIPIENT_NOT_AVAILABLE: {
    kind: "error",
    messages: {
      en: "This recipient is no longer awaiting a response.",
      fr: "Ce destinataire n'attend plus de réponse.",
    },
  },

  INTAKE_LINKS_VERSION_NOT_PUBLIC_FILLABLE: {
    kind: "error",
    messages: {
      en: "This form version requires fields a client cannot fill from a public link.",
      fr: "Cette version de formulaire exige des champs qu\u2019un client ne peut pas remplir depuis un lien public.",
    },
  },

  // --- CLIENT COMMUNICATIONS (chunk 9.AD.1 — 6 codes) -------------------------

  /**
   * Émis quand le contact désigné comme destinataire est introuvable, appartient à
   * un autre tenant, ou n'appartient pas à l'entreprise du dossier. 404.
   *
   * **Anti-leak unique** pour les trois cas. Distinguer « ce contact existe mais
   * ailleurs » de « ce contact n'existe pas » apprendrait à un courtier qu'un
   * homonyme est client d'un cabinet concurrent.
   */
  CLIENT_COMMUNICATIONS_CONTACT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Contact not found for this file.",
      fr: "Contact introuvable pour ce dossier.",
    },
  },

  /**
   * Émis quand le contact retenu n'a pas d'adresse courriel. 422.
   *
   * Volontairement distinct du 404 : le conseiller doit savoir qu'il faut compléter
   * la fiche du contact, pas qu'il s'est trompé de personne.
   */
  CLIENT_COMMUNICATIONS_CONTACT_NO_EMAIL: {
    kind: "error",
    messages: {
      en: "This contact has no email address. Add one before sending.",
      fr: "Ce contact n\u2019a pas d\u2019adresse courriel. Ajoutez-en une avant d\u2019envoyer.",
    },
  },

  /**
   * Émis quand le `missing_information` visé est introuvable, d'un autre tenant, ou
   * hors d'un dossier. 404. Anti-leak unique.
   */
  CLIENT_COMMUNICATIONS_NEED_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Missing information entry not found.",
      fr: "Information manquante introuvable.",
    },
  },

  /**
   * Émis quand on tente de demander au client une information qui ne lui est PAS
   * destinée, ou qui n'est plus ouverte. 409.
   *
   * Les deux refus comptent. Demander au client un élément d'audience `broker`
   * reviendrait à lui réclamer un document interne ; le relancer sur un besoin déjà
   * satisfait ou écarté lui ferait croire qu'on n'a pas vu sa réponse — la façon la
   * plus sûre de lui faire perdre confiance dans nos courriels.
   */
  CLIENT_COMMUNICATIONS_NEED_NOT_ASKABLE: {
    kind: "error",
    messages: {
      en: "This item cannot be requested from the client: it is not addressed to them, or no longer open.",
      fr: "Cet \u00e9l\u00e9ment ne peut pas \u00eatre demand\u00e9 au client : il ne lui est pas destin\u00e9, ou il n\u2019est plus ouvert.",
    },
  },

  /**
   * Émis quand une demande identique est **déjà en file** pour ce besoin et ce
   * destinataire. 409.
   *
   * Remontée applicative de `uq_email_outbox_dedupe_queued`. C'est le double clic,
   * et le retry dont la réponse HTTP s'est perdue : dans les deux cas le courriel
   * part une seule fois.
   *
   * Un renvoi VOLONTAIRE reste possible dès que le premier est parti — l'index ne
   * couvre que les lignes en file.
   */
  CLIENT_COMMUNICATIONS_ALREADY_QUEUED: {
    kind: "error",
    messages: {
      en: "A message for this request is already queued for this recipient.",
      fr: "Un message pour cette demande est d\u00e9j\u00e0 en file pour ce destinataire.",
    },
  },

  /**
   * Émis quand le lien d'intake visé est introuvable, révoqué, expiré, d'un autre
   * tenant, ou ne pointe pas vers le dossier. 422. Anti-leak unique.
   *
   * Envoyer un lien mort au client est pire que ne rien envoyer : il croit que le
   * produit est cassé, et le conseiller ne l'apprend qu'en le rappelant.
   */
  CLIENT_COMMUNICATIONS_LINK_UNUSABLE: {
    kind: "error",
    messages: {
      en: "This intake link cannot be sent: it does not exist, is revoked, or has expired.",
      fr: "Ce lien ne peut pas \u00eatre envoy\u00e9 : il n\u2019existe pas, a \u00e9t\u00e9 r\u00e9voqu\u00e9 ou est expir\u00e9.",
    },
  },

  // --- NOTIFICATIONS (chunk 9.AF.1 — 1 code) ---------------------------------

  /**
   * Émis par `PATCH /v1/notifications/:id/read` quand l'alerte est introuvable,
   * d'un autre tenant, ou adressée à quelqu'un d'autre. 404.
   *
   * **Anti-leak unique** pour les trois cas : distinguer apprendrait à un
   * conseiller qu'une alerte existe pour un collègue, et sur quel dossier.
   */
  NOTIFICATIONS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Notification not found.",
      fr: "Notification introuvable.",
    },
  },

  // --- BOUCLE ASSUREUR (chunk 9.AG.1 — 2 codes) ------------------------------

  /**
   * Émis par `POST /v1/quote-requests/:id/recipients/:rid/respond|decline` quand la
   * demande est `cancelled` ou `expired`. 409.
   *
   * On ne réanime pas une demande annulée parce qu'une réponse arrive en retard :
   * l'annulation est une décision du conseiller, et l'écraser silencieusement la
   * lui retirerait. Le message dit quoi faire — rouvrir une demande — plutôt que
   * de constater un refus.
   */
  QUOTE_REQUEST_RECIPIENTS_RESPONSE_FROZEN: {
    kind: "error",
    messages: {
      en: "This quote request is closed. Open a new request to record a response from this insurer.",
      fr: "Cette demande est clôturée. Ouvrez une nouvelle demande pour consigner une réponse de cet assureur.",
    },
  },

  /**
   * Émis quand la proposition fournie n'appartient pas au dossier de la demande, ou
   * répond déjà à un autre destinataire. 409.
   *
   * Les deux cas partagent un code : les distinguer permettrait de sonder quelles
   * propositions existent ailleurs, et le geste correctif est le même — choisir la
   * bonne proposition.
   */
  QUOTE_REQUEST_RECIPIENTS_SUBMISSION_MISMATCH: {
    kind: "error",
    messages: {
      en: "That proposal does not belong to this request's file, or already answers another insurer.",
      fr: "Cette proposition n'appartient pas au dossier de la demande, ou répond déjà à un autre assureur.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.AI.1 — Rapports de comparaison. Générer, prévisualiser, confirmer.
  //
  // Humain-only : générer un document destiné à un client, et le confirmer, sont
  // des gestes de conseiller. Une clé API qui pourrait confirmer produirait un
  // rapport que personne n'a relu, signé du nom de personne.
  // ---------------------------------------------------------------------------

  /**
   * Émis quand l'identifiant de rapport n'existe pas OU appartient à un autre
   * cabinet. 404 anti-leak unique — distinguer les deux cas permettrait de
   * sonder l'existence de rapports ailleurs.
   */
  COMPARISON_REPORTS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Report not found.",
      fr: "Rapport introuvable.",
    },
  },

  /**
   * Émis à la génération quand le dossier n'existe pas OU appartient à un autre
   * cabinet. 404 anti-leak (parité `COMPARISON_NOTES_PROJECT_NOT_FOUND`).
   */
  COMPARISON_REPORTS_PROJECT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Project not found.",
      fr: "Projet introuvable.",
    },
  },

  /**
   * Émis quand l'appelant n'est pas un utilisateur humain. 403, wording neutre
   * P-9 ADR 0027 : ne distingue ni clé API, ni agent, ni absence d'authentification.
   */
  COMPARISON_REPORTS_FORBIDDEN_USER_ONLY: {
    kind: "error",
    messages: {
      en: "This action requires a human user.",
      fr: "Cette action requiert un utilisateur humain.",
    },
  },

  /**
   * Émis quand on confirme un rapport déjà confirmé. 409.
   *
   * La confirmation est irréversible et nominative : la réémettre écraserait le
   * nom et l'heure de celui qui a effectivement relu. Le message dit le geste
   * disponible — en générer un nouveau — plutôt que de constater un refus.
   */
  COMPARISON_REPORTS_ALREADY_CONFIRMED: {
    kind: "error",
    messages: {
      en: "This report is already confirmed. Generate a new one to reflect more recent data.",
      fr: "Ce rapport est déjà confirmé. Générez-en un nouveau pour refléter des données plus récentes.",
    },
  },

  /**
   * Émis à la génération quand le dossier n'a aucune proposition comparable. 422.
   *
   * Un rapport sans colonne se lirait « aucun assureur n'offre quoi que ce soit »,
   * et rien dans le document ne dirait que c'est faux. Mieux vaut refuser de le
   * produire que de laisser partir cette lecture.
   */
  COMPARISON_REPORTS_NOTHING_TO_REPORT: {
    kind: "error",
    messages: {
      en: "No insurer proposal is ready to compare in this file yet.",
      fr: "Aucune proposition d'assureur n'est encore prête à comparer dans ce dossier.",
    },
  },

  /**
   * Le chemin perdant de l'index partiel `uq_comparison_reports_live_draft` :
   * deux générations simultanées, le brouillon gagnant confirmé entre l'insertion
   * refusée et la relecture. 409, et non un 500 — c'est un état atteignable, pas
   * une panne, et le geste correctif est de recommencer.
   */
  COMPARISON_REPORTS_DRAFT_CONFLICT: {
    kind: "error",
    messages: {
      en: "Another report for this file was being generated at the same time. Try again.",
      fr: "Un autre rapport de ce dossier était en cours de génération au même moment. Réessayez.",
    },
  },
  // ---------------------------------------------------------------------------
  // 9.AJ.1 — Scoring de comparaison. Le score aide à décider, il ne décide pas.
  // ---------------------------------------------------------------------------

  /**
   * Émis quand l'appelant n'a pas le droit de MODIFIER les poids du cabinet. 403,
   * wording neutre P-9 ADR 0027 : ne distingue ni clé API, ni agent, ni rôle
   * insuffisant.
   *
   * Régler les poids change le classement de TOUS les dossiers du cabinet, pas
   * seulement celui qu'on regarde. C'est une décision d'administration, pas un
   * réglage d'écran.
   */
  COMPARISON_WEIGHTS_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Changing scoring weights requires a firm administrator.",
      fr: "Modifier les poids de scoring requiert un administrateur du cabinet.",
    },
  },

  /**
   * Émis quand un réglage vise un critère absent du registre. 422.
   *
   * Le sens d'un critère — plus haut ou plus bas vaut mieux — est de la
   * connaissance métier déclarée en shared. Accepter un poids sur un champ dont
   * personne n'a établi le sens stockerait un réglage mort, et laisserait croire
   * qu'il compte.
   */
  COMPARISON_WEIGHTS_UNKNOWN_CRITERION: {
    kind: "error",
    messages: {
      en: "That criterion cannot be scored: no comparison direction is established for it.",
      fr: "Ce critère ne peut pas être scoré : aucun sens de comparaison n'est établi pour lui.",
    },
  },
  // ---------------------------------------------------------------------------
  // 9.AN.1 — Demande d'information à l'assureur.
  // ---------------------------------------------------------------------------

  /** Le destinataire n'existe pas, ou appartient à un autre cabinet. 404 anti-leak. */
  INFORMATION_REQUESTS_RECIPIENT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Insurer recipient not found.",
      fr: "Destinataire assureur introuvable.",
    },
  },

  /**
   * Émis quand l'information demandée n'est PAS absente. 422.
   *
   * Redemander une valeur déjà fournie, ou une garantie que l'assureur a déjà
   * déclarée non offerte, donnerait l'impression de n'avoir pas lu sa réponse.
   */
  INFORMATION_REQUESTS_NOT_MISSING: {
    kind: "error",
    messages: {
      en: "This information is not missing from the proposal — nothing to request.",
      fr: "Cette information n'est pas absente de la proposition — il n'y a rien à demander.",
    },
  },

  /**
   * Une demande vivante existe déjà pour ce champ chez cet assureur. 409.
   *
   * Le chemin perdant du double clic : l'index partiel a tranché, et le dire vaut
   * mieux qu'un second courriel au même destinataire.
   */
  INFORMATION_REQUESTS_ALREADY_OPEN: {
    kind: "error",
    messages: {
      en: "A request for this information is already open with this insurer.",
      fr: "Une demande pour cette information est déjà ouverte auprès de cet assureur.",
    },
  },

  /**
   * Aucune adresse pour ce destinataire. 422.
   *
   * Ouvrir une demande qu'on ne peut pas envoyer laisserait le conseiller croire
   * qu'il a relancé quelqu'un.
   */
  INFORMATION_REQUESTS_NO_CONTACT: {
    kind: "error",
    messages: {
      en: "This insurer recipient has no email address. Add one before requesting information.",
      fr: "Ce destinataire assureur n'a pas d'adresse courriel. Ajoutez-en une avant de demander l'information.",
    },
  },

  /** Wording neutre P-9 : ne distingue ni clé API, ni agent, ni rôle insuffisant. */
  INFORMATION_REQUESTS_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Requesting information from an insurer requires a human advisor.",
      fr: "Demander une information à un assureur requiert un conseiller humain.",
    },
  },
  // ---------------------------------------------------------------------------
  // 9.AO.1 — Rattachement d'une proposition à son créneau assureur.
  // ---------------------------------------------------------------------------

  /**
   * Le destinataire n'existe pas, appartient à un autre cabinet, ou relève d'un
   * autre dossier. 404 anti-leak unique — distinguer les trois permettrait de
   * sonder les demandes des autres cabinets.
   */
  SUBMISSIONS_RECIPIENT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "That insurer slot was not found in this file, or is no longer open.",
      fr: "Ce créneau assureur est introuvable dans ce dossier, ou n'est plus ouvert.",
    },
  },

  /** Concurrent recipient-link change detected after acquiring parent locks. 409. */
  SUBMISSIONS_RECIPIENT_LINK_CHANGED: {
    kind: "error",
    messages: {
      fr: "Le rattachement de cette proposition a changé. Actualisez les données avant de réessayer.",
      en: "This proposal's recipient link has changed. Refresh the data before trying again.",
    },
  },

  /**
   * Une autre proposition répond déjà à ce créneau. 409.
   *
   * Un créneau assureur reçoit une réponse, pas deux. Le message dit le geste
   * disponible plutôt que de constater un refus.
   */
  SUBMISSIONS_RECIPIENT_ALREADY_ANSWERED: {
    kind: "error",
    messages: {
      en: "Another proposal already answers this insurer slot. Pick another slot, or detach the other proposal from it first.",
      fr: "Une autre proposition répond déjà à ce créneau. Choisissez un autre créneau, ou détachez d'abord la proposition existante.",
    },
  },

  /**
   * Le créneau visé ne correspond pas à l'assureur de la proposition. 422.
   *
   * Rattacher une proposition de Beneva au créneau de Sun Life ferait apparaître
   * la réponse au mauvais endroit — et un assureur silencieux passerait pour
   * ayant répondu.
   */
  SUBMISSIONS_RECIPIENT_INSURER_MISMATCH: {
    kind: "error",
    messages: {
      en: "That slot was sent to a different insurer than this proposal.",
      fr: "Ce créneau a été envoyé à un autre assureur que celui de cette proposition.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.AP.1 — reprise sûre du rattachement
  // ---------------------------------------------------------------------------

  /**
   * Plusieurs créneaux de ce dossier attendent ce même assureur. 422.
   *
   * On refuse plutôt que de choisir : un rattachement muet au mauvais créneau est
   * invisible, et sort la demande perdante des rappels alors qu'un assureur y est
   * encore attendu. Le message demande le geste qui lève l'ambiguïté.
   */
  INTAKE_EVENT_ACTION_AMBIGUOUS_RECIPIENT: {
    kind: "error",
    messages: {
      en: "Several requests in this file are still awaiting this insurer. Pick which one this document answers.",
      fr: "Plusieurs demandes de ce dossier attendent encore cet assureur. Choisissez celle à laquelle ce document répond.",
    },
  },

  /**
   * Corriger un rattachement n'est pas un geste de clé d'API. 403.
   *
   * Réécrire le registre de « qui a répondu à qui » est une décision de courtier —
   * même portée que la demande d'information (9.AN.1).
   */
  SUBMISSIONS_RECIPIENT_LINK_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "Only an advisor can change which request a proposal answers.",
      fr: "Seul un conseiller peut modifier la demande à laquelle une proposition répond.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.AQ.1 — une demande envoyée porte toujours une date de réponse attendue
  // ---------------------------------------------------------------------------

  /**
   * Envoyer sans date de réponse attendue. 422.
   *
   * Le métier de rappels ne scanne que les demandes portant un
   * `expected_response_by` : partir sans date, c'est partir avec les relances
   * **silencieusement désactivées**. On refuse plutôt que de fabriquer une date —
   * la seule personne qui sait quand une réponse est attendue est le conseiller.
   */
  QUOTE_REQUESTS_EXPECTED_RESPONSE_REQUIRED: {
    kind: "error",
    messages: {
      en: "Set the date you expect an answer by before sending — reminders are driven by it.",
      fr: "Indiquez la date à laquelle vous attendez une réponse avant d'envoyer — c'est elle qui déclenche les rappels.",
    },
  },

  /**
   * Effacer la date de réponse attendue d'une demande DÉJÀ envoyée. 422.
   *
   * Modifier la date reste permis, et le rappel suit la nouvelle échéance. La
   * retirer reviendrait à éteindre les relances d'une demande partie chez des
   * assureurs — précisément ce que la garde d'envoi vient d'empêcher.
   */
  QUOTE_REQUESTS_EXPECTED_RESPONSE_NOT_CLEARABLE: {
    kind: "error",
    messages: {
      en: "This request has already gone out. You can move the expected answer date, but not remove it.",
      fr: "Cette demande est déjà partie. Vous pouvez déplacer la date de réponse attendue, mais pas la retirer.",
    },
  },

  // ===========================================================================
  // 9.BJ.1 — Mandat d'étude et conformité d'envoi
  // ===========================================================================

  /**
   * Émis par POST `/v1/quote-requests/:id/send` quand l'entreprise du dossier
   * n'a aucun mandat d'étude signé. 422.
   *
   * C'est le document par lequel le client autorise le courtier à magasiner son
   * dossier et à interroger son assureur actuel. Envoyer sans lui ferait agir le
   * cabinet sans mandat — le défaut que 9.BJ.1 ferme.
   */
  QUOTE_REQUEST_SEND_MANDATE_MISSING: {
    kind: "error",
    messages: {
      en: "No signed study mandate for this client; have it signed before contacting insurers.",
      fr: "Aucun mandat d'étude signé pour ce client ; faites-le signer avant de contacter les assureurs.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/send` quand le mandat existe mais que
   * sa validité est passée. 422.
   *
   * Distinct de `MANDATE_MISSING` à dessein : « aucun mandat » et « mandat
   * périmé » appellent deux gestes différents, et un seul code ferait chercher
   * un document déjà signé.
   */
  QUOTE_REQUEST_SEND_MANDATE_EXPIRED: {
    kind: "error",
    messages: {
      en: "The study mandate for this client has expired; renew it before contacting insurers.",
      fr: "Le mandat d'étude de ce client est échu ; renouvelez-le avant de contacter les assureurs.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/send` quand aucun cahier de charges
   * n'est rattaché. 422.
   *
   * Avant 9.BJ.1 l'envoi était accepté sans cahier : l'assureur recevait une
   * sollicitation sans pièce, c'est-à-dire sans ce qu'on lui demande.
   */
  QUOTE_REQUEST_SEND_SPEC_REQUIRED: {
    kind: "error",
    messages: {
      en: "Attach a specification before sending: an insurer cannot quote what has not been described.",
      fr: "Rattachez un cahier de charges avant d'envoyer : un assureur ne peut pas coter ce qui n'a pas été décrit.",
    },
  },

  /**
   * Émis par POST `/v1/quote-requests/:id/send` quand un requis déclaré bloquant
   * sur le dossier n'est pas satisfait. 422.
   *
   * `details.requirements` nomme les libellés en cause : sans eux, l'utilisateur
   * devrait ouvrir le dossier et comparer lui-même.
   */
  QUOTE_REQUEST_SEND_REQUIREMENTS_UNMET: {
    kind: "error",
    messages: {
      en: "Some blocking requirements of this file are not met.",
      fr: "Des requis bloquants de ce dossier ne sont pas satisfaits.",
    },
  },

  /**
   * Émis quand une clé d'API ou un agent tente d'enregistrer ou de révoquer un
   * mandat. 403.
   *
   * Le périmètre d'une clé bornerait les ressources, pas la capacité d'agir au
   * nom de quelqu'un. Signer engage la conformité du cabinet : c'est une
   * personne qui le fait. Message neutre (P-9, ADR 0027) — il ne distingue pas
   * `api_key` d'agent.
   */
  MANDATE_FORBIDDEN_USER_ONLY: {
    kind: "error",
    messages: {
      en: "This action requires a signed-in user.",
      fr: "Cette action requiert un utilisateur authentifié.",
    },
  },

  /** Émis quand un mandat visé n'existe pas dans le tenant. 404. */
  MANDATE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "Mandate not found.",
      fr: "Mandat introuvable.",
    },
  },

  /**
   * Émis quand on tente de révoquer un mandat déjà révoqué. 409.
   *
   * Idempotent en apparence, mais pas en trace : une seconde révocation
   * écraserait le motif et la date de la première.
   */
  MANDATE_ALREADY_REVOKED: {
    kind: "error",
    messages: {
      en: "This mandate has already been revoked.",
      fr: "Ce mandat a déjà été révoqué.",
    },
  },

  /**
   * Émis quand l'enregistrement d'un mandat déposé vise un fichier qui n'est pas
   * exploitable — absent, d'un autre tenant, ou non abouti. 422.
   */
  MANDATE_SOURCE_FILE_INVALID: {
    kind: "error",
    messages: {
      en: "The provided file cannot be used as a signed mandate.",
      fr: "Le fichier fourni ne peut pas servir de mandat signé.",
    },
  },

  /**
   * Émis par POST `/v1/form-templates/:id/restore` sur un gabarit qui n'est pas
   * archivé. 409.
   */
  FORM_TEMPLATE_NOT_ARCHIVED: {
    kind: "error",
    messages: {
      en: "This form template is not archived.",
      fr: "Ce gabarit de formulaire n'est pas archivé.",
    },
  },

  // -------------------------------------------------------------------------
  // 9.BK.1 — census (liste d'employés)
  // -------------------------------------------------------------------------
  //
  // Aucun de ces codes ne sort par la route publique : elle répond toujours par
  // la réponse d'échec uniforme, y compris quand la cause est un verrou. Dire
  // « ce census est verrouillé » à un inconnu, c'est confirmer qu'il existe.
  // Ils servent le parcours conseiller, authentifié.

  /** 404 — aucun census sur ce dossier, ou hors du tenant courant. */
  CENSUS_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "No employee list found for this file.",
      fr: "Aucune liste d'employés pour ce dossier.",
    },
  },

  /** 409 — un dossier n'a qu'un census (D7). */
  CENSUS_ALREADY_EXISTS: {
    kind: "error",
    messages: {
      en: "This file already has an employee list.",
      fr: "Ce dossier a déjà une liste d'employés.",
    },
  },

  /**
   * 409 — écrire dans un census verrouillé. C'est ce que « arrêté » veut dire :
   * déverrouillez d'abord, en disant pourquoi.
   */
  CENSUS_LOCKED: {
    kind: "error",
    messages: {
      en: "This employee list is locked. Unlock it before making changes.",
      fr: "Cette liste d'employés est verrouillée. Déverrouillez-la avant de la modifier.",
    },
  },

  /**
   * 409 — verrouiller un census que le client n'a pas encore déclaré terminé.
   *
   * `submitted` et `locked` ne sont PAS interchangeables : le premier dit que le
   * client a fini, le second que le cabinet a revu. Sauter le premier ferait
   * verrouiller une saisie en cours.
   */
  CENSUS_NOT_SUBMITTED: {
    kind: "error",
    messages: {
      en: "The client has not declared this employee list complete yet.",
      fr: "Le client n'a pas encore déclaré cette liste d'employés terminée.",
    },
  },

  /** 409 — déverrouiller un census qui ne l'est pas. */
  CENSUS_NOT_LOCKED: {
    kind: "error",
    messages: {
      en: "This employee list is not locked.",
      fr: "Cette liste d'employés n'est pas verrouillée.",
    },
  },

  /** 422 — un déverrouillage sans motif ne laisse aucune trace exploitable. */
  CENSUS_UNLOCK_REASON_REQUIRED: {
    kind: "error",
    messages: {
      en: "Give a reason for unlocking this employee list.",
      fr: "Indiquez la raison du déverrouillage de cette liste d'employés.",
    },
  },

  /**
   * 422 — des lignes sont incomplètes. `details.issues` nomme **chaque ligne et
   * chaque champ** : dire « la liste est incomplète » sans dire où ne permet
   * d'agir sur rien.
   */
  CENSUS_INCOMPLETE: {
    kind: "error",
    messages: {
      en: "Some rows are incomplete; complete them before declaring the list finished.",
      fr: "Certaines lignes sont incomplètes ; complétez-les avant de déclarer la liste terminée.",
    },
  },

  /** 422 — une liste sans personne n'est pas une liste terminée. */
  CENSUS_EMPTY: {
    kind: "error",
    messages: {
      en: "Add at least one employee before declaring the list finished.",
      fr: "Ajoutez au moins un employé avant de déclarer la liste terminée.",
    },
  },

  /** 404 — la ligne visée n'existe pas dans ce census. */
  CENSUS_PARTICIPANT_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "This employee is not in the list.",
      fr: "Cet employé ne figure pas dans la liste.",
    },
  },

  /** 422 — une personne à charge doit se rattacher à un employé de la liste. */
  CENSUS_DEPENDENT_ORPHAN: {
    kind: "error",
    messages: {
      en: "This dependent is not attached to any employee in the list.",
      fr: "Cette personne à charge n'est rattachée à aucun employé de la liste.",
    },
  },

  /**
   * 409 — deux employés au même numéro. Le rattachement des personnes à charge
   * en dépend : ambigu, il serait faux.
   */
  CENSUS_DUPLICATE_EMPLOYEE_NUMBER: {
    kind: "error",
    messages: {
      en: "Another employee already uses this number in this list.",
      fr: "Un autre employé porte déjà ce numéro dans cette liste.",
    },
  },

  /**
   * 403 — un rôle qui n'appartient pas au cabinet touche la liste d'employés.
   *
   * `client_contact` est un utilisateur du **tenant du cabinet** : la résolution
   * de tenant et les RLS ne le distinguent donc pas d'un conseiller. Sans cette
   * garde, la personne-ressource d'un client lirait les noms, dates de
   * naissance, salaires et statuts fumeur des employés de **tous** les clients
   * du cabinet.
   */
  /**
   * 403 — le rôle ne permet pas d'agir sur une demande de soumission.
   *
   * `client_contact` est un utilisateur du **tenant du cabinet** : ni la
   * résolution de tenant, ni les RLS, ni `assertHumanGated` — qui ne filtre que
   * les clés d'API — ne le distinguent d'un conseiller. Sans ce refus, il
   * pouvait ajouter un destinataire à **l'adresse de son choix** puis déclencher
   * l'envoi, avec l'effectif en pièce jointe. Relevé par la revue de sécurité
   * Codex sur la PR #138.
   *
   * Un code distinct de `CENSUS_FORBIDDEN_ROLE` : celui-là parle de listes
   * d'employés, et le dire sur une route de destinataires égarerait.
   */
  /**
   * 403 — un contexte non humain a tenté de **choisir** l'adresse d'un
   * destinataire.
   *
   * Un agent peut ajouter un assureur ; il ne choisit pas où part le courriel.
   * Sans ce refus, une clé d'API posait `evil@example.test` sur un brouillon,
   * et le conseiller qui cliquait « Envoyer » livrait l'effectif — noms, dates
   * de naissance, salaires — à cette adresse, **sans que rien ne paraisse
   * anormal** : c'était bien un humain du cabinet qui envoyait. La garde sur
   * `/send` ne voyait donc rien.
   *
   * **Refus explicite, jamais ignorance silencieuse** : un appelant qui croit
   * avoir fixé l'adresse doit apprendre qu'il ne l'a pas fixée.
   */
  QUOTE_REQUEST_RECIPIENTS_EMAIL_AGENT_FORBIDDEN: {
    kind: "error",
    messages: {
      en: "A non-human caller cannot choose a recipient address; it comes from the insurer registry.",
      fr: "Un appelant non humain ne choisit pas l'adresse d'un destinataire ; elle vient du registre des assureurs.",
    },
  },

  /**
   * 422 — le registre ne donne aucune adresse utilisable pour cet assureur, et
   * l'appelant n'est pas humain.
   *
   * **Aucun repli** vers une adresse fournie par l'agent : ce serait rouvrir par
   * l'échec la porte qu'on vient de fermer. Un humain, lui, peut créer un
   * destinataire sans adresse — l'écran le signale et il la corrige.
   */
  QUOTE_REQUEST_RECIPIENTS_REGISTRY_EMAIL_MISSING: {
    kind: "error",
    messages: {
      en: "The insurer registry has no usable address for this insurer.",
      fr: "Le registre des assureurs ne porte aucune adresse utilisable pour cet assureur.",
    },
  },

  INSERT_FIELDS_NOT_ALLOWED: {
    kind: "error",
    messages: {
      en: "The record cannot be created with these fields.",
      fr: "L’enregistrement ne peut pas être créé avec ces champs.",
    },
  },

  /**
   * 400 — un champ que cette route n'a pas le droit de modifier.
   *
   * **Ce refus n'arrive jamais en usage normal.** Les schémas Zod retirent déjà
   * les clés inconnues ; y parvenir signale soit un défaut de programmation —
   * un schéma relâché, un appel du constructeur avec un corps brut —, soit une
   * tentative. D'où **un seul code transverse** plutôt qu'un par domaine : dix
   * formulations pour une situation qui ne devrait pas exister n'aideraient
   * personne.
   *
   * **Le champ refusé est nommé, la structure interne ne l'est pas.** Savoir
   * quel champ a été rejeté aide à corriger un appel ; connaître la table, la
   * colonne réelle ou la forme de la requête n'aide qu'à en construire une
   * autre.
   */
  UPDATE_FIELD_NOT_ALLOWED: {
    kind: "error",
    messages: {
      en: "This field cannot be modified through this route.",
      fr: "Ce champ ne peut pas être modifié par cette route.",
    },
  },

  QUOTE_REQUESTS_FORBIDDEN_ROLE: {
    kind: "error",
    messages: {
      en: "Your role does not allow acting on quote requests.",
      fr: "Votre rôle ne permet pas d'agir sur les demandes de soumission.",
    },
  },

  CENSUS_FORBIDDEN_ROLE: {
    kind: "error",
    messages: {
      en: "Your role does not allow access to employee lists.",
      fr: "Votre rôle ne donne pas accès aux listes d'employés.",
    },
  },

  /** 409 — plafond de lignes atteint. Une liste, pas un entrepôt. */
  CENSUS_ROW_LIMIT_REACHED: {
    kind: "error",
    messages: {
      en: "This employee list has reached its maximum number of rows.",
      fr: "Cette liste d'employés a atteint son nombre maximal de lignes.",
    },
  },

  // -------------------------------------------------------------------------
  // 9.BK.2 — import du classeur
  // -------------------------------------------------------------------------

  /**
   * 409 — importer dans une liste que le client a déclarée terminée, ou que le
   * cabinet a verrouillée.
   *
   * Un import **remplace** l'effectif. L'autoriser après la déclaration
   * reviendrait à changer sous le verrou ce que quelqu'un a arrêté.
   */
  CENSUS_IMPORT_NOT_DRAFT: {
    kind: "error",
    messages: {
      en: "This employee list is no longer being filled in; unlock it before importing.",
      fr: "Cette liste d'employés n'est plus en saisie ; déverrouillez-la avant d'importer.",
    },
  },

  /** 422 — le classeur ne porte pas de feuille de participants. */
  CENSUS_IMPORT_SHEET_MISSING: {
    kind: "error",
    messages: {
      en: "No participants sheet found in this workbook.",
      fr: "Aucune feuille de participants dans ce classeur.",
    },
  },

  /**
   * 422 — la feuille est là, mais aucune ligne d'en-têtes n'a été reconnue.
   *
   * Distinct de la feuille absente : ici le fichier est le bon, c'est son
   * contenu qui a dérivé — et le geste de correction n'est pas le même.
   */
  CENSUS_IMPORT_HEADER_MISSING: {
    kind: "error",
    messages: {
      en: "The columns of this workbook could not be recognised.",
      fr: "Les colonnes de ce classeur n'ont pas pu être reconnues.",
    },
  },

  /** 422 — le classeur est un gabarit vierge, ou sa feuille est vide. */
  CENSUS_IMPORT_NO_ROWS: {
    kind: "error",
    messages: {
      en: "This workbook contains no employees.",
      fr: "Ce classeur ne contient aucun employé.",
    },
  },

  /**
   * 422 — le fichier déposé n'est pas une archive exploitable, ou dépasse les
   * plafonds de décompression.
   *
   * **Un seul code pour toutes ces causes** : distinguer « ratio trop élevé » de
   * « annuaire menteur » dirait à qui dépose ce qu'on a mesuré dans son fichier,
   * et guiderait la fabrication du suivant. Le journal serveur, lui, porte le
   * motif exact.
   */
  CENSUS_IMPORT_FILE_REJECTED: {
    kind: "error",
    messages: {
      en: "This file could not be read as a spreadsheet.",
      fr: "Ce fichier n'a pas pu être lu comme un classeur.",
    },
  },

  /**
   * 400 — le type de fichier annoncé ne va pas avec l'usage annoncé.
   *
   * Le couplage va dans les deux sens : un classeur ne se dépose que comme
   * source d'effectif, et une source d'effectif n'est qu'un classeur. Sans la
   * seconde moitié, le kind deviendrait un moyen de déposer un PDF que la
   * chaîne d'import n'attend pas ; sans la première, le MIME de classeur
   * s'ouvrirait à tous les kinds.
   */
  FILES_UPLOAD_KIND_MIME_MISMATCH: {
    kind: "error",
    messages: {
      en: "This file type does not match what it is being uploaded for.",
      fr: "Ce type de fichier ne correspond pas à l'usage prévu du dépôt.",
    },
  },

  // ---------------------------------------------------------------------------
  // 9.BK.3 — supprimer un fichier qu'une primitive conserve
  // ---------------------------------------------------------------------------
  //
  // **Un code par relation protégée**, et c'est délibéré.
  //
  // Un code unique aurait forcé un message vague — « ce fichier est conservé » —
  // qui laisse le conseiller chercher pourquoi. Ces quatre faits sont
  // réellement différents : savoir qu'on touche au **mandat signé** plutôt qu'à
  // un classeur change ce qu'on fait ensuite.
  //
  // Le coût est la discipline voulue : ajouter une relation à
  // `RELATIONS_PROTEGEES` **oblige** à écrire ce qu'elle conserve. Une garde qui
  // grandit sans que personne ne formule ce qu'elle protège finirait par le
  // dire mal — ce qui est exactement le défaut que le smoke a relevé, un
  // cahier de charges annoncé comme « source d'un import de liste d'employés ».

  /** 409 — le classeur qui fait foi sur l'origine d'un effectif. */
  FILES_DELETE_REFERENCED_CENSUS_IMPORT: {
    kind: "error",
    messages: {
      en: "This file is kept as the source of an employee list import and cannot be deleted.",
      fr: "Ce fichier est conservé comme source d'un import de liste d'employés et ne peut pas être supprimé.",
    },
  },

  /** 409 — le mandat signé : la pièce qui prouve que le cabinet est mandaté. */
  FILES_DELETE_REFERENCED_MANDATE_ARTIFACT: {
    kind: "error",
    messages: {
      en: "This file is the signed mandate and cannot be deleted.",
      fr: "Ce fichier est le mandat signé et ne peut pas être supprimé.",
    },
  },

  /** 409 — le document dont un mandat a été produit. */
  FILES_DELETE_REFERENCED_MANDATE_SOURCE: {
    kind: "error",
    messages: {
      en: "This file is the source document of a mandate and cannot be deleted.",
      fr: "Ce fichier est le document source d'un mandat et ne peut pas être supprimé.",
    },
  },

  /**
   * 422 — l'effectif est verrouillé, mais aucun classeur n'a été produit pour
   * cette demande.
   *
   * **Un census verrouillé ne signifie pas qu'un export existe** : la production
   * est un geste explicite du conseiller, et l'écran ne doit pas laisser croire
   * l'inverse.
   */
  QUOTE_REQUEST_SEND_CENSUS_EXPORT_REQUIRED: {
    kind: "error",
    messages: {
      en: "The headcount file for insurers has not been generated yet.",
      fr: "Le fichier d'effectif destiné aux assureurs n'a pas encore été produit.",
    },
  },

  /**
   * 422 — l'effectif a changé depuis que le classeur a été produit.
   *
   * On **refuse** plutôt que de régénérer en silence : l'artefact est la preuve
   * de ce qui avait été préparé, et le remplacer sans le dire effacerait la
   * trace. Même doctrine que le cahier de charges.
   */
  /**
   * 422 — on ne peut pas **prouver** que le classeur reflète l'effectif actuel.
   *
   * Distinct de `..._STALE`, et la nuance n'est pas cosmétique : « périmé »
   * **affirme** que l'effectif a changé. Ici on n'affirme rien — l'empreinte de
   * la projection manque, donc la fraîcheur est **invérifiable**. Réutiliser le
   * code du changement ferait dire au produit quelque chose qu'il n'a pas
   * constaté.
   *
   * Le refus est le même : on ne laisse pas partir un classeur dont on ne peut
   * pas répondre. **Aucun repli silencieux vers « prêt ».**
   */
  QUOTE_REQUEST_SEND_CENSUS_EXPORT_UNVERIFIABLE: {
    kind: "error",
    messages: {
      en: "Cannot verify that this workbook reflects the current headcount; generate it again.",
      fr: "Impossible de vérifier que ce classeur reflète l'effectif actuel ; régénérez-le.",
    },
  },

  QUOTE_REQUEST_SEND_CENSUS_EXPORT_STALE: {
    kind: "error",
    messages: {
      en: "The headcount has changed since the file was generated; produce it again.",
      fr: "L'effectif a changé depuis la production du fichier ; produisez-le de nouveau.",
    },
  },

  /**
   * 409 — une pièce déjà partie, ou sur le point de partir, dans un envoi.
   *
   * Le cas que les autres codes ne couvraient pas : un artefact **remplacé**
   * n'est plus la version courante, donc plus référencé par sa table — mais s'il
   * a été joint à un envoi, il est la preuve de **ce qui a réellement été
   * transmis**, et il le reste.
   */
  FILES_DELETE_REFERENCED_SENT_ATTACHMENT: {
    kind: "error",
    messages: {
      en: "This file was attached to an email to insurers and cannot be deleted.",
      fr: "Ce fichier a été joint à un envoi aux assureurs et ne peut pas être supprimé.",
    },
  },

  /** 409 — l'effectif réellement transmis aux assureurs (9.BK.3). */
  FILES_DELETE_REFERENCED_CENSUS_EXPORT: {
    kind: "error",
    messages: {
      en: "This file is the headcount sent to insurers and cannot be deleted.",
      fr: "Ce fichier est l'effectif transmis aux assureurs et ne peut pas être supprimé.",
    },
  },

  /** 409 — ce qui a réellement été transmis aux assureurs. */
  FILES_DELETE_REFERENCED_QUOTE_REQUEST_SPEC: {
    kind: "error",
    messages: {
      en: "This file is the specification sent to insurers and cannot be deleted.",
      fr: "Ce fichier est le cahier de charges transmis aux assureurs et ne peut pas être supprimé.",
    },
  },

  /** 404 — le fichier déposé est introuvable, ou hors du tenant. */
  CENSUS_IMPORT_FILE_NOT_FOUND: {
    kind: "error",
    messages: {
      en: "The uploaded file was not found.",
      fr: "Le fichier déposé est introuvable.",
    },
  },
} as const satisfies Record<string, ErrorCodeEntry>;

/**
 * Tous les codes du registre, typés narrow.
 * `ErrorCode` est l'union de tous les `keyof ERROR_CODES`.
 */
export type ErrorCode = keyof typeof ERROR_CODES;

/**
 * Liste runtime de tous les codes — utile pour les tests d'unicité et le
 * tooling éventuel (ex. génération doc).
 */
export const ALL_ERROR_CODES = Object.keys(ERROR_CODES) as readonly ErrorCode[];

/**
 * Sous-ensemble runtime des codes `error` (pas `warning`). Utile si un
 * caller veut typer une whitelist d'erreurs vs warnings.
 */
export const ERROR_ONLY_CODES = ALL_ERROR_CODES.filter(
  (k) => ERROR_CODES[k].kind === "error",
) as readonly ErrorCode[];

/**
 * Sous-ensemble runtime des codes `warning`.
 */
export const WARNING_ONLY_CODES = ALL_ERROR_CODES.filter(
  (k) => ERROR_CODES[k].kind === "warning",
) as readonly ErrorCode[];
